// Firestore configuration en JavaScript
import { getFirestore, doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, orderBy, limit, deleteDoc, addDoc, serverTimestamp, Timestamp, deleteField, onSnapshot } from 'firebase/firestore';
import { app } from './firebaseConfig';
import { getAuth } from 'firebase/auth';
import { sendForumReplyNotificationEmail } from '../services/emailService';

const db = getFirestore(app);

// Cache mémoire pour données de référence (skills, sectors, countries) — réduit les lectures Firestore
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const FORUM_NOTIF_CACHE_TTL_MS = 30 * 1000; // 30 secondes pour éviter doublons Navbar/ForumPage
const _refCache = {
  skills: { data: null, ts: 0 },
  sectorsWithSub: { data: null, ts: 0 },
  countriesWithSub: { data: null, ts: 0 }
};
const _forumNotifCache = { key: null, data: null, count: null, ts: 0 };

// Fonction pour obtenir les données utilisateur (utilise l'email comme ID)
export const getUserProfile = async (userEmail) => {
  try {
    const docRef = doc(db, 'users', userEmail);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, error: 'Profil non trouvé' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour créer ou mettre à jour le profil utilisateur (utilise l'email comme ID)
export const saveUserProfile = async (userEmail, profileData) => {
  try {
    // Champs à supprimer pour les utilisateurs clients (spécifiques aux prestataires)
    // Note: logoPreview est maintenant autorisé pour les clients aussi
    const fieldsToRemove = [
      'activityDomains',
      'companyName',
      'countries',
      'firstname',
      'lastname',
      'legalStatus',
      'name',
      'presentation',
      'skills',
      'subCountries',
      'subSectors',
      'website',
      'languages',
      'certifications',
      'displayName'
    ];
    
    // Filtrer les données pour ne garder que les champs nécessaires aux clients
    const filteredData = { ...profileData };
    fieldsToRemove.forEach(field => {
      delete filteredData[field];
    });
    
    const docRef = doc(db, 'users', userEmail);
    await setDoc(docRef, filteredData, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour nettoyer les données existantes des utilisateurs clients
export const cleanUserProfile = async (userEmail) => {
  try {
    // Note: logoPreview est maintenant autorisé pour les clients aussi
    const fieldsToRemove = [
      'activityDomains',
      'companyName', 
      'countries',
      'firstname',
      'lastname',
      'legalStatus',
      'name',
      'presentation',
      'skills',
      'subCountries',
      'subSectors',
      'website',
      'languages',
      'certifications',
      'displayName'
    ];
    
    const docRef = doc(db, 'users', userEmail);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const currentData = docSnap.data();
      const cleanedData = { ...currentData };
      
      // Supprimer les champs indésirables
      fieldsToRemove.forEach(field => {
        delete cleanedData[field];
      });
      
      await setDoc(docRef, cleanedData, { merge: false }); // merge: false pour remplacer complètement
      return { success: true, message: 'Profil nettoyé avec succès' };
    } else {
      return { success: false, error: 'Profil non trouvé' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};


// Fonction pour enregistrer un nouveau prestataire de services ou utilisateur
export const registerServiceProvider = async (providerData) => {
  try {
    const { email, isServiceProvider, name } = providerData;
    
    if (!email) {
      return { success: false, error: 'Email requis pour l\'enregistrement' };
    }
    
    if (!name) {
      return { success: false, error: 'Nom de la société requis pour l\'enregistrement' };
    }
    
    // Pour les prestataires de services, sauvegarder dans la collection Providers
    if (isServiceProvider === 'yes') {
      // Créer un ID basé sur le nom de la société (nettoyé pour éviter les caractères spéciaux)
      const companyId = name.toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      
      const providerDocRef = doc(db, 'Providers', companyId);
      
      // Données à enregistrer avec timestamp
      const dataToSave = {
        ...providerData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        role: 'Prestataire',
        isAdmin: false,
        status: 'inactive',
        companyId: companyId
      };
      
      await setDoc(providerDocRef, dataToSave);
      return { success: true, message: 'Prestataire enregistré avec succès dans la collection Providers' };
    } else {
      // Pour les utilisateurs standards, garder l'ancienne logique
      const docRef = doc(db, 'users', email);
      
      const dataToSave = {
        ...providerData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        role: 'Exportateur',
        isAdmin: false,
        status: 'inactive'
      };
      
      await setDoc(docRef, dataToSave);
      return { success: true, message: 'Utilisateur enregistré avec succès' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour des champs spécifiques (utilise l'email comme ID)
export const updateUserProfile = async (userEmail, updates) => {
  try {
    const docRef = doc(db, 'users', userEmail);
    const updatesWithTimestamp = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    await updateDoc(docRef, updatesWithTimestamp);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour enregistrer un message de contact
export const saveContactMessage = async (messageData) => {
  try {
    const { addDoc, collection } = await import('firebase/firestore');
    const messagesRef = collection(db, 'contact_messages');
    const docRef = await addDoc(messagesRef, {
      ...messageData,
      createdAt: new Date().toISOString(),
      status: 'new'
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour ajouter une compétence
// Le document sera créé avec le slug comme ID
export const addSkill = async (skillData) => {
  try {
    const { doc, setDoc, getDoc } = await import('firebase/firestore');
    
    if (!skillData.slug) {
      return { success: false, error: 'Le slug est obligatoire' };
    }
    
    // Vérifier si le slug existe déjà
    const skillRef = doc(db, 'skills', skillData.slug);
    const skillSnap = await getDoc(skillRef);
    
    if (skillSnap.exists()) {
      return { success: false, error: 'Ce slug existe déjà. Veuillez en choisir un autre.' };
    }
    
    // Créer le document avec le slug comme ID
    await setDoc(skillRef, {
      ...skillData,
      createdAt: new Date().toISOString()
    });
    
    return { success: true, id: skillData.slug };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour ajouter un secteur
// Le document sera créé avec le slug comme ID
export const addSector = async (sectorData) => {
  try {
    const { doc, setDoc, getDoc } = await import('firebase/firestore');
    
    if (!sectorData.slug) {
      return { success: false, error: 'Le slug est obligatoire' };
    }
    
    // Vérifier si le slug existe déjà
    const sectorRef = doc(db, 'sectors', sectorData.slug);
    const sectorSnap = await getDoc(sectorRef);
    
    if (sectorSnap.exists()) {
      return { success: false, error: 'Ce slug existe déjà. Veuillez en choisir un autre.' };
    }
    
    // Créer le document avec le slug comme ID
    await setDoc(sectorRef, {
      ...sectorData,
      createdAt: new Date().toISOString()
    });
    
    return { success: true, id: sectorData.slug };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer toutes les compétences (avec cache 5 min pour limiter les lectures Firestore)
export const getSkills = async () => {
  try {
    const now = Date.now();
    if (_refCache.skills.data !== null && now - _refCache.skills.ts < CACHE_TTL_MS) {
      return { success: true, data: _refCache.skills.data };
    }
    if (!db) {
      console.error('❌ [getSkills] La base de données n\'est pas initialisée');
      return { success: false, error: 'Base de données non initialisée', data: [] };
    }
    const { collection, getDocs } = await import('firebase/firestore');
    const skillsRef = collection(db, 'skills');
    let querySnapshot;
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout: La requête a pris trop de temps')), 10000)
      );
      querySnapshot = await Promise.race([getDocs(skillsRef), timeoutPromise]);
    } catch (getDocsError) {
      console.error('❌ [getSkills] Erreur lors de getDocs:', getDocsError);
      throw getDocsError;
    }
    const skills = [];
    querySnapshot.forEach((doc) => {
      skills.push({ id: doc.id, ...doc.data() });
    });
    _refCache.skills = { data: skills, ts: Date.now() };
    return { success: true, data: skills };
  } catch (error) {
    console.error('❌ [getSkills] Erreur:', error?.message || error);
    return { success: false, error: error?.message || 'Erreur inconnue', data: [] };
  }
};

// Fonction pour récupérer tous les secteurs
export const getSectors = async () => {
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const sectorsRef = collection(db, 'sectors');
    const querySnapshot = await getDocs(sectorsRef);
    const sectors = [];
    querySnapshot.forEach((doc) => {
      sectors.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: sectors };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer tous les utilisateurs
export const getAllUsers = async () => {
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: users };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour une compétence
export const updateSkill = async (skillId, skillData) => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    const skillRef = doc(db, 'skills', skillId);
    
    // Ne pas inclure le slug dans la mise à jour (il ne peut pas changer)
    const { slug, ...dataToUpdate } = skillData;
    
    await updateDoc(skillRef, {
      ...dataToUpdate,
      updatedAt: new Date().toISOString()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer une compétence
export const deleteSkill = async (skillId) => {
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    const skillRef = doc(db, 'skills', skillId);
    await deleteDoc(skillRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour un secteur
export const updateSector = async (sectorId, sectorData) => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    const sectorRef = doc(db, 'sectors', sectorId);
    
    // Ne pas inclure le slug dans la mise à jour (il ne peut pas changer)
    const { slug, ...dataToUpdate } = sectorData;
    
    await updateDoc(sectorRef, {
      ...dataToUpdate,
      updatedAt: new Date().toISOString()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer un secteur
export const deleteSector = async (sectorId) => {
  try {
    const { doc, deleteDoc, collection, getDocs, query, where } = await import('firebase/firestore');
    const sectorRef = doc(db, 'sectors', sectorId);
    
    // Supprimer d'abord tous les sous-secteurs associés
    const subsectorsRef = collection(db, 'subsectors');
    const subsectorsQuery = query(subsectorsRef, where('sectorId', '==', sectorId));
    const subsectorsSnapshot = await getDocs(subsectorsQuery);
    
    const deletePromises = subsectorsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    // Puis supprimer le secteur
    await deleteDoc(sectorRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== FONCTIONS POUR LES SOUS-SECTEURS =====

// Fonction pour ajouter un sous-secteur
export const addSubsector = async (subsectorData) => {
  try {
    const { doc, getDoc, collection, addDoc } = await import('firebase/firestore');
    
    if (!subsectorData.sectorId) {
      return { success: false, error: 'L\'ID du secteur parent est obligatoire' };
    }
    
    if (!subsectorData.name) {
      return { success: false, error: 'Le nom du sous-secteur est obligatoire' };
    }
    
    // Créer un slug automatique si non fourni
    const slug = subsectorData.slug || subsectorData.name.toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    // Vérifier si le secteur parent existe
    const sectorRef = doc(db, 'sectors', subsectorData.sectorId);
    const sectorSnap = await getDoc(sectorRef);
    
    if (!sectorSnap.exists()) {
      return { success: false, error: 'Le secteur parent n\'existe pas' };
    }
    
    // Créer le sous-secteur avec un ID auto-généré
    const subsectorRef = collection(db, 'subsectors');
    const newSubsector = {
      name: subsectorData.name,
      slug: slug,
      description: subsectorData.description || '',
      sectorId: subsectorData.sectorId,
      sectorName: sectorSnap.data().name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(subsectorRef, newSubsector);
    
    return { 
      success: true, 
      data: { id: docRef.id, ...newSubsector },
      message: 'Sous-secteur ajouté avec succès' 
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer tous les sous-secteurs d'un secteur
export const getSubsectors = async (sectorId = null) => {
  try {
    const { collection, getDocs, query, where, orderBy } = await import('firebase/firestore');
    const subsectorsRef = collection(db, 'subsectors');
    
    let subsectorsQuery;
    if (sectorId) {
      subsectorsQuery = query(
        subsectorsRef, 
        where('sectorId', '==', sectorId),
        orderBy('name')
      );
    } else {
      subsectorsQuery = query(subsectorsRef, orderBy('name'));
    }
    
    const querySnapshot = await getDocs(subsectorsQuery);
    const subsectors = [];
    querySnapshot.forEach((doc) => {
      subsectors.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: subsectors };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer tous les sous-secteurs groupés par secteur
export const getSubsectorsGrouped = async () => {
  try {
    const { collection, getDocs, orderBy, query } = await import('firebase/firestore');
    const subsectorsRef = collection(db, 'subsectors');
    const subsectorsQuery = query(subsectorsRef, orderBy('sectorName'), orderBy('name'));
    
    const querySnapshot = await getDocs(subsectorsQuery);
    const grouped = {};
    
    querySnapshot.forEach((doc) => {
      const data = { id: doc.id, ...doc.data() };
      const sectorId = data.sectorId;
      
      if (!grouped[sectorId]) {
        grouped[sectorId] = {
          sectorId: sectorId,
          sectorName: data.sectorName,
          subsectors: []
        };
      }
      
      grouped[sectorId].subsectors.push(data);
    });
    
    return { success: true, data: Object.values(grouped) };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour un sous-secteur
export const updateSubsector = async (subsectorId, subsectorData) => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    const subsectorRef = doc(db, 'subsectors', subsectorId);
    
    const dataToUpdate = {
      ...subsectorData,
      updatedAt: new Date().toISOString()
    };
    
    // Ne pas permettre la modification du sectorId
    delete dataToUpdate.sectorId;
    delete dataToUpdate.sectorName;
    
    await updateDoc(subsectorRef, dataToUpdate);
    return { success: true, message: 'Sous-secteur mis à jour avec succès' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer un sous-secteur
export const deleteSubsector = async (subsectorId) => {
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    const subsectorRef = doc(db, 'subsectors', subsectorId);
    await deleteDoc(subsectorRef);
    return { success: true, message: 'Sous-secteur supprimé avec succès' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour ajouter une certification
// Le document sera créé avec le slug comme ID
export const addCertification = async (certificationData) => {
  try {
    const { doc, setDoc, getDoc } = await import('firebase/firestore');
    
    if (!certificationData.slug) {
      return { success: false, error: 'Le slug est obligatoire' };
    }
    
    // Vérifier si le slug existe déjà
    const certificationRef = doc(db, 'certifications', certificationData.slug);
    const certificationSnap = await getDoc(certificationRef);
    
    if (certificationSnap.exists()) {
      return { success: false, error: 'Ce slug existe déjà. Veuillez en choisir un autre.' };
    }
    
    // Créer le document avec le slug comme ID
    await setDoc(certificationRef, {
      ...certificationData,
      createdAt: new Date().toISOString()
    });
    
    return { success: true, id: certificationData.slug };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer toutes les certifications
export const getCertifications = async () => {
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const certificationsRef = collection(db, 'certifications');
    const querySnapshot = await getDocs(certificationsRef);
    const certifications = [];
    querySnapshot.forEach((doc) => {
      certifications.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, data: certifications };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour une certification
export const updateCertification = async (certificationId, certificationData) => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    const certificationRef = doc(db, 'certifications', certificationId);
    
    // Ne pas inclure le slug dans la mise à jour (il ne peut pas changer)
    const { slug, ...dataToUpdate } = certificationData;
    
    await updateDoc(certificationRef, {
      ...dataToUpdate,
      updatedAt: new Date().toISOString()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer une certification
export const deleteCertification = async (certificationId) => {
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    const certificationRef = doc(db, 'certifications', certificationId);
    await deleteDoc(certificationRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// Fonctions pour les langues
// ============================================

// Fonction pour ajouter une langue
export const addLanguage = async (languageData) => {
  try {
    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
    const languagesRef = collection(db, 'languages');
    
    const dataToSave = {
      ...languageData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(languagesRef, dataToSave);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer toutes les langues
export const getLanguages = async () => {
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const languagesRef = collection(db, 'languages');
    const querySnapshot = await getDocs(languagesRef);
    const languages = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: languages };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour mettre à jour une langue
export const updateLanguage = async (languageId, languageData) => {
  try {
    const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const languageRef = doc(db, 'languages', languageId);
    const dataToUpdate = {
      ...languageData,
      updatedAt: serverTimestamp()
    };
    await updateDoc(languageRef, dataToUpdate);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer une langue
export const deleteLanguage = async (languageId) => {
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    const languageRef = doc(db, 'languages', languageId);
    await deleteDoc(languageRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// Fonctions pour les pays
// ============================================

// Fonction pour ajouter un pays
export const addCountry = async (countryData) => {
  try {
    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
    const countriesRef = collection(db, 'countries');
    
    const dataToSave = {
      ...countryData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(countriesRef, dataToSave);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer tous les pays
export const getCountries = async () => {
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const countriesRef = collection(db, 'countries');
    const querySnapshot = await getDocs(countriesRef);
    const countries = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: countries };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour mettre à jour un pays
export const updateCountry = async (countryId, countryData) => {
  try {
    const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const countryRef = doc(db, 'countries', countryId);
    const dataToUpdate = {
      ...countryData,
      updatedAt: serverTimestamp()
    };
    await updateDoc(countryRef, dataToUpdate);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer un pays
export const deleteCountry = async (countryId) => {
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    const countryRef = doc(db, 'countries', countryId);
    await deleteDoc(countryRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer tous les providers (accès public - pas besoin d'authentification)
export const getAllProviders = async () => {
  try {
    if (!db) {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!db) {
        return { success: false, error: 'Base de données non initialisée', data: [] };
      }
    }
    
    const { collection, getDocs } = await import('firebase/firestore');
    const providersRef = collection(db, 'Providers');
    const querySnapshot = await getDocs(providersRef);
    
    const providers = [];
    querySnapshot.forEach((doc) => {
      providers.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: providers };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[getAllProviders] Erreur:', error?.message || error);
    }
    
    // Vérifier si c'est une erreur de permission (ne devrait pas arriver car accès public)
    if (error.code === 'permission-denied' || error.code === 'PERMISSION_DENIED') {
      return { 
        success: false, 
        error: 'Erreur de permission. Vérifiez que les règles Firestore permettent la lecture publique de la collection Providers.', 
        data: [] 
      };
    }
    
    return { success: false, error: error.message || 'Erreur inconnue lors du chargement des prestataires', data: [] };
  }
};

/**
 * Abonnement en temps réel à la collection Providers.
 * Les cartes Services sont mises à jour dès qu'un prestataire modifie son profil.
 * @param {function(Array)} onUpdate - Callback appelé avec la liste des prestataires à chaque changement
 * @param {function(Error)} [onError] - Callback optionnel en cas d'erreur
 * @returns {function} - Fonction à appeler pour se désabonner
 */
export const subscribeToProviders = (onUpdate, onError) => {
  if (!db) {
    console.warn('⚠️ [subscribeToProviders] db non initialisé');
    if (onError) onError(new Error('Base de données non initialisée'));
    return () => {};
  }
  const providersRef = collection(db, 'Providers');
  const unsubscribe = onSnapshot(
    providersRef,
    (querySnapshot) => {
      const providers = [];
      querySnapshot.forEach((docSnap) => {
        providers.push({ id: docSnap.id, ...docSnap.data() });
      });
      onUpdate(providers);
    },
    (err) => {
      console.error('❌ [subscribeToProviders] Erreur:', err);
      if (onError) onError(err);
    }
  );
  return unsubscribe;
};

// ===== FONCTIONS POUR LES SOUS-SECTEURS =====

// Fonction pour ajouter un sous-secteur
export const addSubSector = async (parentSectorId, subSectorData) => {
  try {
    const { doc, getDoc, collection, addDoc } = await import('firebase/firestore');
    
    if (!parentSectorId || !subSectorData.name || !subSectorData.slug) {
      return { success: false, error: 'Le secteur parent, le nom et le slug sont obligatoires' };
    }
    
    // Vérifier que le secteur parent existe
    const parentSectorRef = doc(db, 'sectors', parentSectorId);
    const parentSectorSnap = await getDoc(parentSectorRef);
    
    if (!parentSectorSnap.exists()) {
      return { success: false, error: 'Le secteur parent n\'existe pas' };
    }
    
    // Créer le sous-secteur dans une sous-collection
    const subSectorsRef = collection(db, 'sectors', parentSectorId, 'subSectors');
    const subSectorDataWithTimestamp = {
      ...subSectorData,
      parentSectorId: parentSectorId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(subSectorsRef, subSectorDataWithTimestamp);
    
    return { 
      success: true, 
      data: { id: docRef.id, ...subSectorDataWithTimestamp },
      message: 'Sous-secteur ajouté avec succès'
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer tous les sous-secteurs d'un secteur
export const getSubSectors = async (parentSectorId) => {
  try {
    console.log(`🔍 [getSubSectors] Récupération des sous-secteurs pour le secteur: ${parentSectorId}`);
    const { collection, getDocs } = await import('firebase/firestore');
    const subSectorsRef = collection(db, 'sectors', parentSectorId, 'subSectors');
    console.log(`📡 [getSubSectors] Référence créée pour: sectors/${parentSectorId}/subSectors`);
    
    const querySnapshot = await getDocs(subSectorsRef);
    console.log(`📊 [getSubSectors] ${querySnapshot.size} sous-secteur(s) trouvé(s)`);
    
    const subSectors = [];
    querySnapshot.forEach((doc) => {
      const subSectorData = { id: doc.id, ...doc.data() };
      subSectors.push(subSectorData);
      console.log(`  ✅ Sous-secteur ajouté: ${subSectorData.name || doc.id}`);
    });
    
    if (subSectors.length === 0) {
      console.warn(`⚠️ [getSubSectors] Aucun sous-secteur trouvé pour le secteur ${parentSectorId}`);
    }
    
    return { success: true, data: subSectors };
  } catch (error) {
    console.error(`❌ [getSubSectors] Erreur lors de la récupération des sous-secteurs pour ${parentSectorId}:`, error);
    console.error(`❌ [getSubSectors] Code d'erreur:`, error.code);
    console.error(`❌ [getSubSectors] Message:`, error.message);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour récupérer tous les secteurs avec leurs sous-secteurs (cache 5 min + sous-requêtes en parallèle)
export const getSectorsWithSubSectors = async () => {
  try {
    const now = Date.now();
    if (_refCache.sectorsWithSub.data !== null && now - _refCache.sectorsWithSub.ts < CACHE_TTL_MS) {
      return { success: true, data: _refCache.sectorsWithSub.data };
    }
    if (!db) {
      return { success: false, error: 'Base de données non initialisée', data: [] };
    }
    const { collection, getDocs } = await import('firebase/firestore');
    const sectorsRef = collection(db, 'sectors');
    let sectorsSnapshot;
    try {
      sectorsSnapshot = await getDocs(sectorsRef);
    } catch (getDocsError) {
      console.error('❌ [getSectorsWithSubSectors] Erreur getDocs:', getDocsError?.message);
      throw getDocsError;
    }
    // Sous-requêtes en parallèle pour limiter le temps total (même nombre de lectures, moins d'attente)
    const subResults = await Promise.all(
      sectorsSnapshot.docs.map((sectorDoc) => getSubSectors(sectorDoc.id))
    );
    const sectors = sectorsSnapshot.docs.map((sectorDoc, i) => {
      const sectorData = { id: sectorDoc.id, ...sectorDoc.data() };
      sectorData.subSectors = subResults[i]?.success ? subResults[i].data : [];
      return sectorData;
    });
    _refCache.sectorsWithSub = { data: sectors, ts: Date.now() };
    return { success: true, data: sectors };
  } catch (error) {
    console.error('❌ [getSectorsWithSubSectors] Erreur:', error?.message || error);
    return { success: false, error: error?.message || 'Erreur inconnue', data: [] };
  }
};

// Fonction pour mettre à jour un sous-secteur
export const updateSubSector = async (parentSectorId, subSectorId, subSectorData) => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    const subSectorRef = doc(db, 'sectors', parentSectorId, 'subSectors', subSectorId);
    
    const dataToUpdate = {
      ...subSectorData,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(subSectorRef, dataToUpdate);
    return { success: true, message: 'Sous-secteur mis à jour avec succès' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer un sous-secteur
export const deleteSubSector = async (parentSectorId, subSectorId) => {
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    const subSectorRef = doc(db, 'sectors', parentSectorId, 'subSectors', subSectorId);
    await deleteDoc(subSectorRef);
    return { success: true, message: 'Sous-secteur supprimé avec succès' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer tous les sous-secteurs d'un secteur (utile pour la suppression en cascade)
export const deleteAllSubSectors = async (parentSectorId) => {
  try {
    const { collection, getDocs, doc, deleteDoc } = await import('firebase/firestore');
    const subSectorsRef = collection(db, 'sectors', parentSectorId, 'subSectors');
    const querySnapshot = await getDocs(subSectorsRef);
    
    const deletePromises = [];
    querySnapshot.forEach((subSectorDoc) => {
      deletePromises.push(deleteDoc(doc(db, 'sectors', parentSectorId, 'subSectors', subSectorDoc.id)));
    });
    
    await Promise.all(deletePromises);
    return { success: true, message: 'Tous les sous-secteurs ont été supprimés' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== FONCTIONS POUR LES SOUS-PAYS =====

// Fonction pour ajouter un sous-pays
export const addSubCountry = async (parentCountryId, subCountryData) => {
  try {
    const { doc, getDoc, collection, addDoc } = await import('firebase/firestore');
    
    if (!parentCountryId || !subCountryData.name || !subCountryData.slug) {
      return { success: false, error: 'Le pays parent, le nom et le slug sont obligatoires' };
    }
    
    // Vérifier que le pays parent existe
    const parentCountryRef = doc(db, 'countries', parentCountryId);
    const parentCountrySnap = await getDoc(parentCountryRef);
    
    if (!parentCountrySnap.exists()) {
      return { success: false, error: 'Le pays parent n\'existe pas' };
    }
    
    // Créer le sous-pays dans une sous-collection
    const subCountriesRef = collection(db, 'countries', parentCountryId, 'subCountries');
    const subCountryDataWithTimestamp = {
      ...subCountryData,
      parentCountryId: parentCountryId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(subCountriesRef, subCountryDataWithTimestamp);
    
    return { 
      success: true, 
      data: { id: docRef.id, ...subCountryDataWithTimestamp },
      message: 'Sous-pays ajouté avec succès'
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer tous les sous-pays d'un pays
export const getSubCountries = async (parentCountryId) => {
  try {
    console.log(`🔍 [getSubCountries] Récupération des sous-pays pour le pays: ${parentCountryId}`);
    const { collection, getDocs } = await import('firebase/firestore');
    const subCountriesRef = collection(db, 'countries', parentCountryId, 'subCountries');
    console.log(`📡 [getSubCountries] Référence créée pour: countries/${parentCountryId}/subCountries`);
    
    const querySnapshot = await getDocs(subCountriesRef);
    console.log(`📊 [getSubCountries] ${querySnapshot.size} sous-pays trouvé(s)`);
    
    const subCountries = [];
    querySnapshot.forEach((doc) => {
      const subCountryData = { id: doc.id, ...doc.data() };
      subCountries.push(subCountryData);
      console.log(`  ✅ Sous-pays ajouté: ${subCountryData.name || doc.id}`);
    });
    
    if (subCountries.length === 0) {
      console.warn(`⚠️ [getSubCountries] Aucun sous-pays trouvé pour le pays ${parentCountryId}`);
    }
    
    return { success: true, data: subCountries };
  } catch (error) {
    console.error(`❌ [getSubCountries] Erreur lors de la récupération des sous-pays pour ${parentCountryId}:`, error);
    console.error(`❌ [getSubCountries] Code d'erreur:`, error.code);
    console.error(`❌ [getSubCountries] Message:`, error.message);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour récupérer tous les pays avec leurs sous-pays (cache 5 min + sous-requêtes en parallèle)
export const getCountriesWithSubCountries = async () => {
  try {
    const now = Date.now();
    if (_refCache.countriesWithSub.data !== null && now - _refCache.countriesWithSub.ts < CACHE_TTL_MS) {
      return { success: true, data: _refCache.countriesWithSub.data };
    }
    if (!db) {
      return { success: false, error: 'Base de données non initialisée', data: [] };
    }
    const { collection, getDocs } = await import('firebase/firestore');
    const countriesRef = collection(db, 'countries');
    let countriesSnapshot;
    try {
      countriesSnapshot = await getDocs(countriesRef);
    } catch (getDocsError) {
      console.error('❌ [getCountriesWithSubCountries] Erreur getDocs:', getDocsError?.message);
      throw getDocsError;
    }
    const subResults = await Promise.all(
      countriesSnapshot.docs.map((countryDoc) => getSubCountries(countryDoc.id))
    );
    const countries = countriesSnapshot.docs.map((countryDoc, i) => {
      const countryData = { id: countryDoc.id, ...countryDoc.data() };
      countryData.subCountries = subResults[i]?.success ? subResults[i].data : [];
      return countryData;
    });
    countries.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));
    countries.forEach((c) => {
      if (c.subCountries && c.subCountries.length > 0) {
        c.subCountries.sort((a, b) => (typeof a === 'object' && a?.name ? a.name : String(a)).localeCompare(typeof b === 'object' && b?.name ? b.name : String(b), 'fr'));
      }
    });
    _refCache.countriesWithSub = { data: countries, ts: Date.now() };
    return { success: true, data: countries };
  } catch (error) {
    console.error('❌ [getCountriesWithSubCountries] Erreur:', error?.message || error);
    return { success: false, error: error?.message || 'Erreur inconnue', data: [] };
  }
};

/** Invalide le cache des données de référence (à appeler après mise à jour skills/sectors/countries en admin). */
export const clearRefCache = () => {
  _refCache.skills = { data: null, ts: 0 };
  _refCache.sectorsWithSub = { data: null, ts: 0 };
  _refCache.countriesWithSub = { data: null, ts: 0 };
};

// Fonction pour mettre à jour un sous-pays
export const updateSubCountry = async (parentCountryId, subCountryId, subCountryData) => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    const subCountryRef = doc(db, 'countries', parentCountryId, 'subCountries', subCountryId);
    
    const dataToUpdate = {
      ...subCountryData,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(subCountryRef, dataToUpdate);
    return { success: true, message: 'Sous-pays mis à jour avec succès' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer un sous-pays
export const deleteSubCountry = async (parentCountryId, subCountryId) => {
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    const subCountryRef = doc(db, 'countries', parentCountryId, 'subCountries', subCountryId);
    await deleteDoc(subCountryRef);
    return { success: true, message: 'Sous-pays supprimé avec succès' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer tous les sous-pays d'un pays (utile pour la suppression en cascade)
export const deleteAllSubCountries = async (parentCountryId) => {
  try {
    const { collection, getDocs, doc, deleteDoc } = await import('firebase/firestore');
    const subCountriesRef = collection(db, 'countries', parentCountryId, 'subCountries');
    const querySnapshot = await getDocs(subCountriesRef);
    
    const deletePromises = [];
    querySnapshot.forEach((subCountryDoc) => {
      deletePromises.push(deleteDoc(doc(db, 'countries', parentCountryId, 'subCountries', subCountryDoc.id)));
    });
    
    await Promise.all(deletePromises);
    return { success: true, message: 'Tous les sous-pays ont été supprimés' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// FONCTIONS POUR LES PROFILS PROVIDERS
// ============================================

// Fonction pour récupérer le profil d'un provider par email
export const getProviderProfile = async (email) => {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    
    // Les prestataires peuvent être stockés avec companyId comme clé, donc on cherche par email
    const providersRef = collection(db, 'Providers');
    const q = query(providersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const providerDoc = querySnapshot.docs[0];
      return {
        success: true,
        data: { id: providerDoc.id, ...providerDoc.data() }
      };
    } else {
      // Essayer aussi avec l'email comme ID direct (pour compatibilité)
      const { doc, getDoc } = await import('firebase/firestore');
      const providerRef = doc(db, 'Providers', email);
      const providerSnap = await getDoc(providerRef);
      
      if (providerSnap.exists()) {
        return {
          success: true,
          data: { id: providerSnap.id, ...providerSnap.data() }
        };
      }
      
      return {
        success: false,
        error: 'Profil provider non trouvé'
      };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour sauvegarder le profil d'un provider
export const saveProviderProfile = async (documentIdOrEmail, profileData) => {
  try {
    const { doc, setDoc, collection, query, where, getDocs } = await import('firebase/firestore');
    
    let providerDocRef;
    
    // Si documentIdOrEmail ressemble à un email, chercher le document par email
    if (documentIdOrEmail.includes('@')) {
      const providersRef = collection(db, 'Providers');
      const q = query(providersRef, where('email', '==', documentIdOrEmail));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const providerDoc = querySnapshot.docs[0];
        providerDocRef = doc(db, 'Providers', providerDoc.id);
      } else {
        // Si pas trouvé, utiliser l'email comme ID direct (pour compatibilité)
        providerDocRef = doc(db, 'Providers', documentIdOrEmail);
      }
    } else {
      // Utiliser directement comme ID de document
      providerDocRef = doc(db, 'Providers', documentIdOrEmail);
    }
    
    // Extraire l'email du profileData si disponible
    const email = profileData.email || documentIdOrEmail;
    // Synchroniser "name" avec companyName pour que l'affichage (cartes Services) soit à jour partout
    const companyName = profileData.companyName != null ? String(profileData.companyName).trim() : '';
    
    const dataToSave = {
      ...profileData,
      email: email,
      name: companyName || profileData.name || '',
      companyName: companyName || profileData.companyName || '',
      updatedAt: new Date().toISOString()
    };
    
    await setDoc(providerDocRef, dataToSave, { merge: true });
    
    // Sync isAdmin dans users pour que les règles Firestore reconnaissent les prestataires admin
    if (email && typeof profileData.isAdmin === 'boolean') {
      const userDocRef = doc(db, 'users', email);
      await setDoc(userDocRef, { email, isAdmin: profileData.isAdmin, updatedAt: new Date().toISOString() }, { merge: true });
    }
    
    return {
      success: true,
      message: 'Profil provider mis à jour avec succès'
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer complètement un utilisateur (Firestore + Firebase Auth)
export const deleteUserCompletely = async (userEmail) => {
  try {
    console.log('🗑️ Suppression complète de l\'utilisateur:', userEmail);
    
    // 1. Supprimer de la collection 'users' dans Firestore
    const userDocRef = doc(db, 'users', userEmail);
    await deleteDoc(userDocRef);
    console.log('✅ Utilisateur supprimé de Firestore (users)');
    
    // 2. Supprimer de la collection 'Providers' dans Firestore (si c'est un prestataire)
    try {
      const providersQuery = query(collection(db, 'Providers'), where('email', '==', userEmail));
      const providersSnapshot = await getDocs(providersQuery);
      
      if (!providersSnapshot.empty) {
        for (const providerDoc of providersSnapshot.docs) {
          await deleteDoc(providerDoc.ref);
          console.log('✅ Prestataire supprimé de Firestore (Providers)');
        }
      }
    } catch (providerError) {
      console.log('ℹ️ Aucun document provider trouvé ou erreur:', providerError.message);
    }
    
    // 3. Supprimer de Firebase Authentication
    try {
      getAuth(); // Initialiser auth pour référence future
      
      // Trouver l'utilisateur par email
      const userQuery = query(collection(db, 'users'), where('email', '==', userEmail));
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        // Note: La suppression de Firebase Auth nécessite que l'utilisateur soit connecté
        // ou que nous ayons des privilèges d'admin. Pour l'instant, on supprime seulement de Firestore.
        console.log('ℹ️ Suppression de Firebase Auth nécessite des privilèges admin');
        console.log('ℹ️ L\'utilisateur devra se reconnecter pour que la suppression soit effective');
      }
    } catch (authError) {
      console.log('ℹ️ Erreur lors de la suppression Firebase Auth:', authError.message);
    }
    
    return {
      success: true,
      message: 'Utilisateur supprimé avec succès de Firestore. La suppression de Firebase Auth nécessite des privilèges admin.'
    };
    
  } catch (error) {
    console.error('❌ Erreur lors de la suppression complète:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============================================
// FONCTIONS POUR LA MESSAGERIE
// ============================================

// Fonction pour créer ou récupérer une conversation entre deux utilisateurs
export const getOrCreateConversation = async (user1Email, user2Email) => {
  try {
    const { collection, query, where, getDocs, addDoc } = await import('firebase/firestore');
    
    // Créer un ID de conversation unique basé sur les deux emails (toujours dans le même ordre)
    const participants = [user1Email, user2Email].sort();
    const conversationId = participants.join('_');
    
    // Chercher si la conversation existe déjà
    const conversationsRef = collection(db, 'conversations');
    const q = query(conversationsRef, where('conversationId', '==', conversationId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Conversation existe déjà
      const conversationDoc = querySnapshot.docs[0];
      return {
        success: true,
        data: { id: conversationDoc.id, ...conversationDoc.data() }
      };
    } else {
      // Créer une nouvelle conversation
      const newConversation = {
        conversationId: conversationId,
        participants: participants,
        participant1: user1Email,
        participant2: user2Email,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastMessage: null,
        lastMessageTime: null
      };
      
      const docRef = await addDoc(conversationsRef, newConversation);
      return {
        success: true,
        data: { id: docRef.id, ...newConversation }
      };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer toutes les conversations d'un utilisateur
export const getUserConversations = async (userEmail) => {
  try {
    const { collection, query, where, getDocs, orderBy } = await import('firebase/firestore');
    
    const conversationsRef = collection(db, 'conversations');
    
    // Utiliser deux requêtes : une pour participant1 et une pour participant2
    // Puis combiner les résultats
    try {
      // Requête pour les conversations où l'utilisateur est participant1
      let q1;
      try {
        q1 = await getDocs(query(
          conversationsRef,
          where('participant1', '==', userEmail),
          orderBy('updatedAt', 'desc')
        ));
      } catch (orderByError) {
        // Si l'index n'existe pas, récupérer sans orderBy
        q1 = await getDocs(query(
          conversationsRef,
          where('participant1', '==', userEmail)
        ));
      }
      
      // Requête pour les conversations où l'utilisateur est participant2
      let q2;
      try {
        q2 = await getDocs(query(
          conversationsRef,
          where('participant2', '==', userEmail),
          orderBy('updatedAt', 'desc')
        ));
      } catch (orderByError) {
        // Si l'index n'existe pas, récupérer sans orderBy
        q2 = await getDocs(query(
          conversationsRef,
          where('participant2', '==', userEmail)
        ));
      }
      
      const conversationsMap = new Map();
      
      // Ajouter les conversations de la première requête
      q1.forEach((doc) => {
        conversationsMap.set(doc.id, { id: doc.id, ...doc.data() });
      });
      
      // Ajouter les conversations de la deuxième requête
      q2.forEach((doc) => {
        conversationsMap.set(doc.id, { id: doc.id, ...doc.data() });
      });
      
      // Convertir en tableau et trier
      const conversations = Array.from(conversationsMap.values());
      conversations.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      });
      
      return { success: true, data: conversations };
    } catch (error) {
      console.error('Erreur lors de la récupération des conversations:', error);
      // Fallback : récupérer toutes les conversations et filtrer côté client
      const allConversations = await getDocs(conversationsRef);
      const conversations = [];
      
      allConversations.forEach((doc) => {
        const data = doc.data();
        if (data.participant1 === userEmail || data.participant2 === userEmail) {
          conversations.push({ id: doc.id, ...data });
        }
      });
      
      // Trier par updatedAt
      conversations.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      });
      
      return { success: true, data: conversations };
    }
  } catch (error) {
    console.error('Erreur lors de la récupération des conversations:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour envoyer un message
export const sendMessage = async (conversationDocId, senderEmail, receiverEmail, messageText) => {
  try {
    const { collection, addDoc, doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    
    // Ajouter le message à la sous-collection messages
    const messagesRef = collection(db, 'conversations', conversationDocId, 'messages');
    const newMessage = {
      senderEmail: senderEmail,
      receiverEmail: receiverEmail,
      text: messageText,
      createdAt: new Date().toISOString(),
      timestamp: serverTimestamp(),
      read: false
    };
    
    const messageDocRef = await addDoc(messagesRef, newMessage);
    
    // Mettre à jour la conversation avec le dernier message
    const conversationRef = doc(db, 'conversations', conversationDocId);
    await updateDoc(conversationRef, {
      lastMessage: messageText,
      lastMessageTime: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    return {
      success: true,
      data: { id: messageDocRef.id, ...newMessage }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour récupérer les messages d'une conversation
export const getConversationMessages = async (conversationDocId) => {
  try {
    const { collection, query, orderBy, getDocs } = await import('firebase/firestore');
    
    const messagesRef = collection(db, 'conversations', conversationDocId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: messages };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour marquer les messages comme lus
export const markMessagesAsRead = async (conversationDocId, userEmail) => {
  try {
    const { collection, query, where, getDocs, updateDoc, doc } = await import('firebase/firestore');
    
    const messagesRef = collection(db, 'conversations', conversationDocId, 'messages');
    const q = query(
      messagesRef,
      where('receiverEmail', '==', userEmail),
      where('read', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    const updatePromises = [];
    
    querySnapshot.forEach((messageDoc) => {
      const messageRef = doc(db, 'conversations', conversationDocId, 'messages', messageDoc.id);
      updatePromises.push(updateDoc(messageRef, { read: true }));
    });
    
    await Promise.all(updatePromises);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Fonction pour envoyer un message interne à un utilisateur
export const sendInternalMessageToUser = async (recipientEmail, messageContent, messageSubject, senderName = 'Système') => {
  try {
    // Normaliser l'email du destinataire
    const normalizedRecipientEmail = recipientEmail.toLowerCase().trim();
    
    // Créer l'ID du message (timestamp + random)
    const messageId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Utiliser serverTimestamp pour la date de création
    const timestamp = serverTimestamp();
    
    // Données du message reçu (pour le destinataire)
    const receivedMessageData = {
      id: messageId,
      senderEmail: 'system@exportservice.com',
      senderName: senderName,
      receiverEmail: normalizedRecipientEmail,
      receiverName: normalizedRecipientEmail.split('@')[0],
      subject: messageSubject,
      message: messageContent,
      createdAt: timestamp,
      status: 'new' // Nouveau message non lu
    };

    // Sauvegarder dans la collection receivedMessages de l'utilisateur
    const userReceivedMessagesRef = collection(db, 'users', normalizedRecipientEmail, 'receivedMessages');
    await addDoc(userReceivedMessagesRef, receivedMessageData);

    console.log(`✅ [sendInternalMessageToUser] Message envoyé à ${normalizedRecipientEmail}`);
    return { success: true, messageId: messageId };
  } catch (error) {
    console.error('❌ Erreur lors de l\'envoi du message interne:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour envoyer un message d'un utilisateur à un provider
export const sendMessageToProvider = async (senderEmail, providerId, messageContent, messageSubject, providerData) => {
  try {
    // Créer l'ID du message (timestamp + random)
    const messageId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Utiliser serverTimestamp pour la date de création
    const timestamp = serverTimestamp();
    
    // Données du message envoyé (pour l'expéditeur)
    const sentMessageData = {
      id: messageId,
      senderEmail: senderEmail,
      receiverId: providerId, // ID du provider (companyId)
      receiverEmail: providerData.email || '',
      receiverName: providerData.name || providerData.companyName || 'Provider',
      subject: messageSubject,
      message: messageContent,
      createdAt: timestamp,
      status: 'read' // Les messages envoyés sont toujours considérés comme lus
    };

    // Données du message reçu (pour le destinataire)
    const receivedMessageData = {
      id: messageId,
      senderEmail: senderEmail,
      senderName: providerData.senderName || senderEmail,
      receiverId: providerId,
      receiverEmail: providerData.email || '',
      receiverName: providerData.name || providerData.companyName || 'Provider',
      subject: messageSubject,
      message: messageContent,
      createdAt: timestamp,
      status: 'new' // Nouveau message non lu
    };

    // 1. Sauvegarder dans la collection sentMessages de l'utilisateur (messages envoyés)
    const userSentMessagesRef = collection(db, 'users', senderEmail, 'sentMessages');
    await addDoc(userSentMessagesRef, sentMessageData);

    // 2. Sauvegarder dans la collection receivedMessages du provider (messages reçus)
    const providerReceivedMessagesRef = collection(db, 'Providers', providerId, 'receivedMessages');
    await addDoc(providerReceivedMessagesRef, receivedMessageData);

    return { success: true, messageId: messageId };
  } catch (error) {
    console.error('❌ Erreur lors de l\'envoi du message:', error);
    console.error('❌ Code d\'erreur:', error.code);
    console.error('❌ Détails:', {
      senderEmail,
      providerId,
      errorMessage: error.message,
      errorCode: error.code
    });
    
    // Message d'erreur plus détaillé
    let errorMessage = error.message || 'Erreur inconnue lors de l\'envoi du message';
    if (error.code === 'permission-denied' || error.code === 'PERMISSION_DENIED') {
      errorMessage = 'Permission refusée. Vérifiez que vous êtes bien connecté et que les règles Firestore sont correctement configurées.';
    }
    
    return { success: false, error: errorMessage };
  }
};

// Fonction pour récupérer les messages envoyés par un utilisateur
export const getUserSentMessages = async (userEmail) => {
  try {
    const messagesRef = collection(db, 'users', userEmail, 'sentMessages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const messages = [];
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: messages };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des messages envoyés:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour récupérer les messages reçus par un utilisateur
export const getUserReceivedMessages = async (userEmail) => {
  try {
    const messagesRef = collection(db, 'users', userEmail, 'receivedMessages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const messages = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Préserver l'ID personnalisé du message (stocké dans les données) et ajouter l'ID du document Firestore
      messages.push({ 
        id: data.id || doc.id, // Utiliser l'ID personnalisé s'il existe, sinon l'ID du document
        docId: doc.id, // ID du document Firestore pour les mises à jour
        ...data 
      });
    });
    
    return { success: true, data: messages };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des messages reçus:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour récupérer les messages envoyés par un provider
export const getProviderSentMessages = async (providerId) => {
  try {
    const messagesRef = collection(db, 'Providers', providerId, 'sentMessages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const messages = [];
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: messages };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des messages envoyés:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour récupérer les messages reçus par un provider
export const getProviderReceivedMessages = async (providerId) => {
  try {
    const messagesRef = collection(db, 'Providers', providerId, 'receivedMessages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const messages = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Préserver l'ID personnalisé du message (stocké dans les données) et ajouter l'ID du document Firestore
      messages.push({ 
        id: data.id || doc.id, // Utiliser l'ID personnalisé s'il existe, sinon l'ID du document
        docId: doc.id, // ID du document Firestore pour les mises à jour
        ...data 
      });
    });
    
    return { success: true, data: messages };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des messages reçus:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour marquer un message comme lu dans receivedMessages d'un utilisateur
export const markUserMessageAsRead = async (userEmail, messageId) => {
  try {
    const messageRef = doc(db, 'users', userEmail, 'receivedMessages', messageId);
    console.log('📝 Mise à jour du message dans Firestore:', { userEmail, messageId, status: 'read' });
    await updateDoc(messageRef, { status: 'read', read: true }); // Mettre à jour les deux champs pour compatibilité
    console.log('✅ Message mis à jour avec succès dans Firestore');
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors du marquage du message comme lu:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour marquer un message comme lu dans receivedMessages d'un provider
export const markProviderMessageAsRead = async (providerId, messageId) => {
  try {
    const messageRef = doc(db, 'Providers', providerId, 'receivedMessages', messageId);
    console.log('📝 Mise à jour du message provider dans Firestore:', { providerId, messageId, status: 'read' });
    await updateDoc(messageRef, { status: 'read', read: true }); // Mettre à jour les deux champs pour compatibilité
    console.log('✅ Message provider mis à jour avec succès dans Firestore');
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors du marquage du message comme lu:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour marquer une discussion comme ouverte dans Firestore
// providerId peut être string | null | undefined
export const markDiscussionAsOpened = async (userEmail, discussionId, isProvider = false, providerId = null) => {
  // Normaliser providerId : convertir undefined en null pour compatibilité
  // Accepter string, null ou undefined et convertir tout en null si falsy
  const normalizedProviderId = providerId && typeof providerId === 'string' ? providerId : null;
  try {
    if (!userEmail || !discussionId) {
      return { success: false, error: 'userEmail et discussionId sont requis' };
    }

    // Créer une collection pour stocker les discussions ouvertes par utilisateur
    const openedDiscussionsRef = collection(db, 'users', userEmail, 'openedDiscussions');
    
    // Vérifier si la discussion est déjà marquée comme ouverte
    const q = query(openedDiscussionsRef, where('discussionId', '==', discussionId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      // Ajouter la discussion comme ouverte avec timestamp
      await addDoc(openedDiscussionsRef, {
        discussionId: discussionId,
        openedAt: serverTimestamp(),
        isProvider: isProvider,
        providerId: normalizedProviderId
      });
      console.log('✅ Discussion marquée comme ouverte dans Firestore:', discussionId);
    } else {
      console.log('ℹ️ Discussion déjà marquée comme ouverte:', discussionId);
    }
    
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors du marquage de la discussion comme ouverte:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour obtenir toutes les discussions ouvertes par un utilisateur
export const getOpenedDiscussions = async (userEmail) => {
  try {
    if (!userEmail) {
      return { success: false, error: 'userEmail est requis' };
    }

    const openedDiscussionsRef = collection(db, 'users', userEmail, 'openedDiscussions');
    const querySnapshot = await getDocs(openedDiscussionsRef);
    
    const openedDiscussions = new Set();
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.discussionId) {
        openedDiscussions.add(data.discussionId);
      }
    });
    
    console.log('📦 Discussions ouvertes chargées depuis Firestore:', Array.from(openedDiscussions));
    return { success: true, data: openedDiscussions };
  } catch (error) {
    console.error('❌ Erreur lors du chargement des discussions ouvertes:', error);
    return { success: false, error: error.message };
  }
};

// Cache court pour le comptage des messages non lus (Navbar + MessagingPage)
const MESSAGE_COUNT_CACHE_TTL_MS = 20 * 1000;
const _messageCountCache = { key: null, count: null, ts: 0 };

export const getUnreadMessageCount = async (userEmail, providerId = null) => {
  try {
    const cacheKey = `${userEmail}|${providerId || ''}`;
    if (_messageCountCache.key === cacheKey && Date.now() - _messageCountCache.ts < MESSAGE_COUNT_CACHE_TTL_MS) {
      return { success: true, count: _messageCountCache.count };
    }
    let totalUnread = 0;
    const userMessagesRef = collection(db, 'users', userEmail, 'receivedMessages');
    const userSnapshot = await getDocs(query(userMessagesRef, where('status', '==', 'new')));
    userSnapshot.forEach((doc) => {
      const data = doc.data();
      if (!data.parentMessageId) totalUnread++;
    });
    if (providerId && typeof providerId === 'string' && providerId.trim() !== '') {
      const providerMessagesRef = collection(db, 'Providers', providerId, 'receivedMessages');
      const providerSnapshot = await getDocs(query(providerMessagesRef, where('status', '==', 'new')));
      providerSnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.parentMessageId) totalUnread++;
      });
    }
    _messageCountCache.key = cacheKey;
    _messageCountCache.count = totalUnread;
    _messageCountCache.ts = Date.now();
    return { success: true, count: totalUnread };
  } catch (error) {
    if (error?.code !== 'permission-denied' && !String(error?.message || '').includes('permission')) {
      console.error('❌ [getUnreadMessageCount]', error?.message || error);
    }
    return { success: false, error: error.message, count: 0 };
  }
};

// Limite le nombre de messages envoyés pris en compte (réduit les lectures Firestore)
const MAX_SENT_FOR_REPLIES = 50;

async function _countUnreadRepliesForRole(sentRef, receivedRef) {
  const sentQ = query(sentRef, orderBy('createdAt', 'desc'), limit(MAX_SENT_FOR_REPLIES));
  const sentSnap = await getDocs(sentQ);
  const sentIds = new Set();
  sentSnap.forEach((d) => {
    const data = d.data();
    if (!data.parentMessageId && data.id) sentIds.add(data.id);
  });
  if (sentIds.size === 0) return 0;
  let receivedSnap;
  try {
    receivedSnap = await getDocs(query(receivedRef, where('status', '==', 'new')));
  } catch (e) {
    if (e.code === 'failed-precondition') receivedSnap = await getDocs(receivedRef);
    else throw e;
  }
  let count = 0;
  receivedSnap.forEach((d) => {
    const data = d.data();
    if (sentIds.has(data.parentMessageId) && (data.status === 'new' || !data.read)) count++;
  });
  return count;
}

export const getUnreadRepliesCount = async (userEmail, providerId = null) => {
  try {
    const userSentRef = collection(db, 'users', userEmail, 'sentMessages');
    const userReceivedRef = collection(db, 'users', userEmail, 'receivedMessages');
    let total = await _countUnreadRepliesForRole(userSentRef, userReceivedRef);
    if (providerId && typeof providerId === 'string' && providerId.trim() !== '') {
      const providerSentRef = collection(db, 'Providers', providerId, 'sentMessages');
      const providerReceivedRef = collection(db, 'Providers', providerId, 'receivedMessages');
      total += await _countUnreadRepliesForRole(providerSentRef, providerReceivedRef);
    }
    return { success: true, count: total };
  } catch (error) {
    if (error?.code !== 'permission-denied' && !String(error?.message || '').includes('permission')) {
      console.error('❌ [getUnreadRepliesCount]', error?.message || error);
    }
    return { success: false, error: error.message, count: 0 };
  }
};

// ============================================
// FONCTIONS POUR LE FORUM
// ============================================

// Fonction pour obtenir toutes les catégories du forum
export const getForumCategories = async () => {
  try {
    const forumRef = collection(db, 'forum');
    const q = query(forumRef, orderBy('order', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const categories = [];
    querySnapshot.forEach((doc) => {
      categories.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: categories };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des catégories:', error);
    // Si erreur d'index, essayer sans orderBy
    if (error.code === 'failed-precondition') {
      try {
        const forumRef = collection(db, 'forum');
        const querySnapshot = await getDocs(forumRef);
        const categories = [];
        querySnapshot.forEach((doc) => {
          categories.push({ id: doc.id, ...doc.data() });
        });
        categories.sort((a, b) => (a.order || 0) - (b.order || 0));
        return { success: true, data: categories };
      } catch (e) {
        return { success: false, error: e.message, data: [] };
      }
    }
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour créer une catégorie de forum
export const createForumCategory = async (categoryData) => {
  try {
    // Utiliser l'ID de la catégorie comme nom du document
    const categoryId = categoryData.id || categoryData.name.toLowerCase().replace(/\s+/g, '-');
    const categoryRef = doc(db, 'forum', categoryId);
    
    // Vérifier si la catégorie existe déjà
    const categorySnap = await getDoc(categoryRef);
    if (categorySnap.exists()) {
      return { success: false, error: 'Cette catégorie existe déjà' };
    }
    
    // Obtenir le dernier ordre si non spécifié
    let order = categoryData.order;
    if (order === undefined || order === null) {
      const existingCategories = await getForumCategories();
      if (existingCategories.success) {
        const maxOrder = existingCategories.data.reduce((max, cat) => 
          Math.max(max, cat.order || 0), 0);
        order = maxOrder + 1;
      } else {
        order = 1;
      }
    }
    
    const newCategory = {
      name: categoryData.name,
      description: categoryData.description || '',
      icon: categoryData.icon || 'bi-chat-dots',
      order: order,
      parentId: categoryData.parentId || null, // null pour catégorie principale, ID pour sous-catégorie
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(categoryRef, newCategory);
    return { success: true, id: categoryId };
  } catch (error) {
    console.error('❌ Erreur lors de la création de la catégorie:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour une catégorie
export const updateForumCategory = async (categoryId, updates) => {
  try {
    const categoryRef = doc(db, 'forum', categoryId);
    const updatesWithTimestamp = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    await updateDoc(categoryRef, updatesWithTimestamp);
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la mise à jour de la catégorie:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer une catégorie
export const deleteForumCategory = async (categoryId) => {
  try {
    // Vérifier s'il y a des topics dans cette catégorie
    const topicsResult = await getForumTopics(categoryId);
    if (topicsResult.success && topicsResult.data.length > 0) {
      // Supprimer tous les topics et leurs réponses
      for (const topic of topicsResult.data) {
        try {
          // Supprimer toutes les réponses du topic
          const repliesResult = await getForumReplies(topic.id, categoryId);
          if (repliesResult.success) {
            for (const reply of repliesResult.data) {
              try {
                await deleteForumReply(topic.id, categoryId, reply.id);
              } catch (error) {
                console.error(`Erreur lors de la suppression de la réponse ${reply.id}:`, error);
              }
            }
          }
          
          // Supprimer le topic
          await deleteForumTopic(topic.id, categoryId);
        } catch (error) {
          console.error(`Erreur lors de la suppression du topic ${topic.id}:`, error);
        }
      }
    }
    
    // Supprimer la catégorie
    const categoryRef = doc(db, 'forum', categoryId);
    await deleteDoc(categoryRef);
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la suppression de la catégorie:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour obtenir les sous-catégories d'une catégorie
export const getForumSubcategories = async (parentCategoryId) => {
  try {
    const forumRef = collection(db, 'forum');
    const q = query(
      forumRef,
      where('parentId', '==', parentCategoryId),
      orderBy('order', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    const subcategories = [];
    querySnapshot.forEach((doc) => {
      subcategories.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: subcategories };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des sous-catégories:', error);
    // Si erreur d'index, essayer sans orderBy
    if (error.code === 'failed-precondition') {
      try {
        const forumRef = collection(db, 'forum');
        const q = query(forumRef, where('parentId', '==', parentCategoryId));
        const querySnapshot = await getDocs(q);
        const subcategories = [];
        querySnapshot.forEach((doc) => {
          subcategories.push({ id: doc.id, ...doc.data() });
        });
        subcategories.sort((a, b) => (a.order || 0) - (b.order || 0));
        return { success: true, data: subcategories };
      } catch (e) {
        return { success: false, error: e.message, data: [] };
      }
    }
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour obtenir les topics d'une catégorie (seulement les approuvés)
export const getForumTopics = async (categoryId, limit = 50) => {
  try {
    const topicsRef = collection(db, 'forum', categoryId, 'topics');
    // Filtrer uniquement les sujets approuvés (approved === true)
    // Note: Si approved n'existe pas, on considère que c'est approuvé pour la rétrocompatibilité
    const q = query(
      topicsRef,
      where('approved', '==', true),
      orderBy('lastActivity', 'desc'),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const topics = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Inclure aussi les sujets sans champ approved (rétrocompatibilité)
      if (data.approved === true || data.approved === undefined) {
        topics.push({ id: doc.id, categoryId: categoryId, ...data });
      }
    });
    
    return { success: true, data: topics.slice(0, limit) };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des topics:', error);
    // Si erreur d'index, essayer sans orderBy sur lastActivity ou sans where
    if (error.code === 'failed-precondition') {
      try {
        const topicsRef = collection(db, 'forum', categoryId, 'topics');
        // Essayer avec where mais sans orderBy sur lastActivity
        const q = query(
          topicsRef,
          where('approved', '==', true),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const topics = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.approved === true || data.approved === undefined) {
            topics.push({ id: doc.id, categoryId: categoryId, ...data });
          }
        });
        return { success: true, data: topics.slice(0, limit) };
      } catch (e) {
        // Si ça échoue encore, essayer sans where (rétrocompatibilité)
        try {
          const topicsRef = collection(db, 'forum', categoryId, 'topics');
          const q = query(
            topicsRef,
            orderBy('createdAt', 'desc')
          );
          const querySnapshot = await getDocs(q);
          const topics = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            // Filtrer manuellement : inclure seulement les approuvés ou ceux sans champ approved
            if (data.approved === true || data.approved === undefined) {
              topics.push({ id: doc.id, categoryId: categoryId, ...data });
            }
          });
          return { success: true, data: topics.slice(0, limit) };
        } catch (e2) {
          return { success: false, error: e2.message, data: [] };
        }
      }
    }
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour obtenir tous les topics (toutes catégories) - seulement les approuvés
export const getAllForumTopics = async (limit = 50) => {
  try {
    // Récupérer toutes les catégories
    const categoriesResult = await getForumCategories();
    if (!categoriesResult.success) {
      return { success: false, error: 'Impossible de récupérer les catégories', data: [] };
    }
    
    const allTopics = [];
    
    // Parcourir toutes les catégories et récupérer leurs topics
    for (const category of categoriesResult.data) {
      try {
        const topicsRef = collection(db, 'forum', category.id, 'topics');
        // Essayer avec where pour filtrer les approuvés
        const q = query(
          topicsRef,
          where('approved', '==', true),
          orderBy('lastActivity', 'desc')
        );
        const querySnapshot = await getDocs(q);
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.approved === true || data.approved === undefined) {
            allTopics.push({ id: doc.id, categoryId: category.id, ...data });
          }
        });
      } catch (error) {
        // Si erreur d'index, essayer avec createdAt ou sans where
        if (error.code === 'failed-precondition') {
          try {
            const topicsRef = collection(db, 'forum', category.id, 'topics');
            const q = query(
              topicsRef,
              where('approved', '==', true),
              orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              if (data.approved === true || data.approved === undefined) {
                allTopics.push({ id: doc.id, categoryId: category.id, ...data });
              }
            });
          } catch (e) {
            // Si ça échoue encore, essayer sans where (rétrocompatibilité)
            try {
              const topicsRef = collection(db, 'forum', category.id, 'topics');
              const q = query(
                topicsRef,
                orderBy('createdAt', 'desc')
              );
              const querySnapshot = await getDocs(q);
              querySnapshot.forEach((doc) => {
                const data = doc.data();
                // Filtrer manuellement : inclure seulement les approuvés ou ceux sans champ approved
                if (data.approved === true || data.approved === undefined) {
                  allTopics.push({ id: doc.id, categoryId: category.id, ...data });
                }
              });
            } catch (e2) {
              console.error(`Erreur pour la catégorie ${category.id}:`, e2);
            }
          }
        } else {
          console.error(`Erreur pour la catégorie ${category.id}:`, error);
        }
      }
    }
    
    // Trier par lastActivity et limiter
    allTopics.sort((a, b) => {
      const aTime = a.lastActivity?.toDate?.() || a.lastActivity?.seconds || 0;
      const bTime = b.lastActivity?.toDate?.() || b.lastActivity?.seconds || 0;
      return bTime - aTime;
    });
    
    return { success: true, data: allTopics.slice(0, limit) };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des topics:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour obtenir les sujets gérés par l'admin : approuvés OU désactivés (pour la section "Gestion des sujets")
export const getManagedForumTopics = async (limit = 500) => {
  try {
    const categoriesResult = await getForumCategories();
    if (!categoriesResult.success) {
      return { success: false, error: 'Impossible de récupérer les catégories', data: [] };
    }
    const managedTopics = [];
    for (const category of categoriesResult.data) {
      try {
        const topicsRef = collection(db, 'forum', category.id, 'topics');
        const q = query(topicsRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Inclure : approuvés (visibles) OU désactivés (plus visibles mais restent en gestion)
          const isApproved = data.approved === true || data.approved === undefined;
          const isDeactivated = data.deactivated === true;
          if (isApproved || isDeactivated) {
            managedTopics.push({ id: doc.id, categoryId: category.id, ...data });
          }
        });
      } catch (error) {
        if (error.code === 'failed-precondition') {
          try {
            const topicsRef = collection(db, 'forum', category.id, 'topics');
            const querySnapshot = await getDocs(topicsRef);
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              const isApproved = data.approved === true || data.approved === undefined;
              const isDeactivated = data.deactivated === true;
              if (isApproved || isDeactivated) {
                managedTopics.push({ id: doc.id, categoryId: category.id, ...data });
              }
            });
          } catch (e) {
            console.error(`Erreur pour la catégorie ${category.id}:`, e);
          }
        } else {
          console.error(`Erreur pour la catégorie ${category.id}:`, error);
        }
      }
    }
    managedTopics.sort((a, b) => {
      const aTime = a.lastActivity?.toDate?.() || a.lastActivity?.seconds || a.createdAt?.toDate?.() || a.createdAt?.seconds || 0;
      const bTime = b.lastActivity?.toDate?.() || b.lastActivity?.seconds || b.createdAt?.toDate?.() || b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
    return { success: true, data: managedTopics.slice(0, limit) };
  } catch (error) {
    console.error('❌ Erreur getManagedForumTopics:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour réactiver un sujet désactivé (admin)
export const activateForumTopic = async (topicId, categoryId) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);
    if (!topicSnap.exists()) {
      return { success: false, error: 'Topic non trouvé' };
    }
    await updateDoc(topicRef, {
      approved: true,
      approvedAt: serverTimestamp(),
      deactivated: deleteField()
    });
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la réactivation du topic:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour obtenir un topic spécifique
export const getForumTopic = async (topicId, categoryId) => {
  try {
    if (!categoryId) {
      // Si categoryId n'est pas fourni, chercher dans toutes les catégories
      const categoriesResult = await getForumCategories();
      if (!categoriesResult.success) {
        return { success: false, error: 'Impossible de récupérer les catégories' };
      }
      
      for (const category of categoriesResult.data) {
        try {
          const topicRef = doc(db, 'forum', category.id, 'topics', topicId);
          const topicSnap = await getDoc(topicRef);
          
          if (topicSnap.exists()) {
            return { success: true, data: { id: topicSnap.id, categoryId: category.id, ...topicSnap.data() } };
          }
        } catch (e) {
          // Continuer à chercher dans les autres catégories
        }
      }
      
      return { success: false, error: 'Topic non trouvé' };
    } else {
      const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
      const topicSnap = await getDoc(topicRef);
      
      if (topicSnap.exists()) {
        return { success: true, data: { id: topicSnap.id, categoryId: categoryId, ...topicSnap.data() } };
      } else {
        return { success: false, error: 'Topic non trouvé' };
      }
    }
  } catch (error) {
    console.error('❌ Erreur lors de la récupération du topic:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour notifier les prestataires correspondants à un sujet
export const notifyProvidersForTopic = async (topicData, topicId, categoryId, senderEmail, senderName) => {
  try {
    // Vérifier s'il y a des tags dans le sujet
    const hasTags = 
      (topicData.skills && topicData.skills.length > 0) ||
      (topicData.activityDomains && topicData.activityDomains.length > 0) ||
      (topicData.countries && topicData.countries.length > 0) ||
      (topicData.subSectors && topicData.subSectors.length > 0) ||
      (topicData.subCountries && topicData.subCountries.length > 0);

    if (!hasTags) {
      console.log('ℹ️ [notifyProvidersForTopic] Aucun tag dans le sujet, pas de notification envoyée');
      return { success: true, notifiedCount: 0 };
    }

    // Récupérer tous les prestataires
    const providersResult = await getAllProviders();
    if (!providersResult.success || !providersResult.data || providersResult.data.length === 0) {
      console.log('ℹ️ [notifyProvidersForTopic] Aucun prestataire trouvé');
      return { success: true, notifiedCount: 0 };
    }

    const providers = providersResult.data;
    const matchingProviders = [];

    // Filtrer les prestataires qui ont au moins un tag en commun
    for (const provider of providers) {
      let hasMatchingTag = false;

      // Vérifier les compétences (skills)
      if (topicData.skills && topicData.skills.length > 0 && provider.skills && provider.skills.length > 0) {
        const matchingSkills = topicData.skills.filter(skill => 
          provider.skills.some((pSkill: string) => 
            pSkill && skill && pSkill.toLowerCase().trim() === skill.toLowerCase().trim()
          )
        );
        if (matchingSkills.length > 0) {
          hasMatchingTag = true;
        }
      }

      // Vérifier les domaines d'activités
      if (!hasMatchingTag && topicData.activityDomains && topicData.activityDomains.length > 0 && provider.activityDomains && provider.activityDomains.length > 0) {
        const matchingDomains = topicData.activityDomains.filter(domain => 
          provider.activityDomains.some((pDomain: string) => 
            pDomain && domain && pDomain.toLowerCase().trim() === domain.toLowerCase().trim()
          )
        );
        if (matchingDomains.length > 0) {
          hasMatchingTag = true;
        }
      }

      // Vérifier les sous-secteurs
      if (!hasMatchingTag && topicData.subSectors && topicData.subSectors.length > 0 && provider.subSectors && provider.subSectors.length > 0) {
        const matchingSubSectors = topicData.subSectors.filter(subSector => 
          provider.subSectors.some((pSubSector: string) => 
            pSubSector && subSector && pSubSector.toLowerCase().trim() === subSector.toLowerCase().trim()
          )
        );
        if (matchingSubSectors.length > 0) {
          hasMatchingTag = true;
        }
      }

      // Vérifier les pays
      if (!hasMatchingTag && topicData.countries && topicData.countries.length > 0 && provider.countries && provider.countries.length > 0) {
        const matchingCountries = topicData.countries.filter(country => 
          provider.countries.some((pCountry: string) => 
            pCountry && country && pCountry.toLowerCase().trim() === country.toLowerCase().trim()
          )
        );
        if (matchingCountries.length > 0) {
          hasMatchingTag = true;
        }
      }

      // Vérifier les sous-pays
      if (!hasMatchingTag && topicData.subCountries && topicData.subCountries.length > 0 && provider.subCountries && provider.subCountries.length > 0) {
        const matchingSubCountries = topicData.subCountries.filter(subCountry => 
          provider.subCountries.some((pSubCountry: string) => 
            pSubCountry && subCountry && pSubCountry.toLowerCase().trim() === subCountry.toLowerCase().trim()
          )
        );
        if (matchingSubCountries.length > 0) {
          hasMatchingTag = true;
        }
      }

      if (hasMatchingTag) {
        matchingProviders.push(provider);
      }
    }

    console.log(`📧 [notifyProvidersForTopic] ${matchingProviders.length} prestataire(s) correspondant(s) trouvé(s) pour le sujet "${topicData.title}"`);

    // Envoyer un message à chaque prestataire correspondant
    let notifiedCount = 0;
    const notificationPromises = [];

    for (const provider of matchingProviders) {
      // Obtenir l'ID du provider (companyId ou id)
      const providerId = provider.companyId || provider.id;
      if (!providerId) {
        console.warn(`⚠️ [notifyProvidersForTopic] Provider sans ID: ${provider.email || provider.name || 'Inconnu'}`);
        continue;
      }

      // Créer le message
      const messageSubject = `Nouveau sujet correspondant à vos services: ${topicData.title}`;
      const messageContent = `Bonjour,\n\nUn nouveau sujet a été créé dans le forum qui correspond à vos services.\n\n` +
        `Titre: ${topicData.title}\n` +
        `Catégorie: ${categoryId}\n` +
        `Auteur: ${senderName || senderEmail}\n\n` +
        `Vous pouvez consulter ce sujet dans le forum.\n\n` +
        `Cordialement,\nL'équipe ExportService`;

      // Envoyer le message de manière asynchrone (ne pas bloquer)
      notificationPromises.push(
        sendMessageToProvider(
          senderEmail || 'system@exportservice.com',
          providerId,
          messageContent,
          messageSubject,
          {
            ...provider,
            senderName: senderName || 'Système'
          }
        ).then(result => {
          if (result.success) {
            notifiedCount++;
            console.log(`✅ [notifyProvidersForTopic] Message envoyé à ${provider.name || provider.companyName || provider.email}`);
          } else {
            console.error(`❌ [notifyProvidersForTopic] Erreur lors de l'envoi à ${provider.name || provider.companyName || provider.email}:`, result.error);
          }
        }).catch(error => {
          console.error(`❌ [notifyProvidersForTopic] Exception lors de l'envoi à ${provider.name || provider.companyName || provider.email}:`, error);
        })
      );
    }

    // Attendre que tous les messages soient envoyés (mais ne pas bloquer si certains échouent)
    await Promise.allSettled(notificationPromises);

    console.log(`✅ [notifyProvidersForTopic] ${notifiedCount} message(s) envoyé(s) avec succès sur ${matchingProviders.length} prestataire(s)`);
    return { success: true, notifiedCount };
  } catch (error) {
    console.error('❌ [notifyProvidersForTopic] Erreur lors de la notification des prestataires:', error);
    // Ne pas faire échouer la création du sujet si la notification échoue
    return { success: false, error: error.message, notifiedCount: 0 };
  }
};

// Fonction pour créer un nouveau topic
export const createForumTopic = async (topicData) => {
  try {
    if (!topicData.categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }
    
    const topicsRef = collection(db, 'forum', topicData.categoryId, 'topics');
    // Normaliser l'email pour garantir la correspondance avec les notifications
    const normalizedAuthorEmail = topicData.authorEmail?.toLowerCase().trim() || topicData.authorEmail;
    const isAuthorAdmin = topicData.createdByAdmin === true;
    const newTopic = {
      title: topicData.title,
      content: topicData.content,
      authorEmail: normalizedAuthorEmail,
      authorName: topicData.authorName,
      createdAt: serverTimestamp(),
      lastActivity: serverTimestamp(),
      replyCount: 0,
      viewCount: 0,
      favoriteCount: 0,
      favoritedBy: [],
      isLocked: false,
      isPinned: false,
      approved: isAuthorAdmin, // Sujets créés par un admin sont approuvés directement
      createdByAdmin: isAuthorAdmin || undefined,
      ...(isAuthorAdmin && { approvedAt: serverTimestamp() }),
      // Tags sélectionnés (optionnels)
      skills: topicData.skills || [],
      activityDomains: topicData.activityDomains || [],
      countries: topicData.countries || [],
      subSectors: topicData.subSectors || [],
      subCountries: topicData.subCountries || []
    };
    
    const docRef = await addDoc(topicsRef, newTopic);
    
    // Envoyer un message d'attente d'approbation uniquement si l'auteur n'est pas admin
    if (!isAuthorAdmin) {
      setTimeout(async () => {
        try {
          const messageSubject = 'Votre sujet est en attente d\'approbation / Your topic is pending approval';
          const messageContent = `Bonjour / Hello,\n\nVotre sujet "${topicData.title}" a été créé avec succès et est actuellement en attente d'approbation par un administrateur.\nYour topic "${topicData.title}" has been created successfully and is currently pending approval by an administrator.\n\nUne fois approuvé, il sera visible dans le forum.\nOnce approved, it will be visible in the forum.\n\nMerci de votre patience.\nThank you for your patience.\n\nCordialement / Best regards,\nL'équipe ExportService / The ExportService team`;
          
          await sendInternalMessageToUser(
            normalizedAuthorEmail,
            messageContent,
            messageSubject,
            'Système ExportService'
          );
        } catch (error) {
          console.error('❌ Erreur lors de l\'envoi du message de confirmation (non bloquant):', error);
        }
      }, 500);
    }
    
    // Notifier les prestataires correspondants (de manière asynchrone, ne pas bloquer)
    if (topicData.skills?.length > 0 || topicData.activityDomains?.length > 0 || 
        topicData.countries?.length > 0 || topicData.subSectors?.length > 0 || 
        topicData.subCountries?.length > 0) {
      // Envoyer les notifications en arrière-plan (ne pas attendre)
      setTimeout(async () => {
        try {
          await notifyProvidersForTopic(
            topicData,
            docRef.id,
            topicData.categoryId,
            normalizedAuthorEmail,
            topicData.authorName
          );
        } catch (error) {
          console.error('❌ Erreur lors de la notification des prestataires (non bloquant):', error);
        }
      }, 1000); // Petit délai pour ne pas bloquer la réponse
    }
    
    return { success: true, id: docRef.id, categoryId: topicData.categoryId };
  } catch (error) {
    console.error('❌ Erreur lors de la création du topic:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour obtenir les réponses d'un topic
export const getForumReplies = async (topicId, categoryId, limit = 100) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis', data: [] };
    }
    
    const repliesRef = collection(db, 'forum', categoryId, 'topics', topicId, 'replies');
    const q = query(
      repliesRef,
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    const replies = [];
    querySnapshot.forEach((doc) => {
      replies.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: replies.slice(0, limit) };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des réponses:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// ============================================
// FONCTIONS POUR LES NOTIFICATIONS DU FORUM
// ============================================

// Fonction pour créer une notification de réponse au forum
// IMPORTANT: Cette fonction doit être définie AVANT createForumReply car elle est appelée dedans
export const createForumNotification = async (topicOwnerEmail, topicId, categoryId, topicTitle, replyAuthorName, replyAuthorEmail) => {
  try {
    if (!topicOwnerEmail || !topicId || !categoryId) {
      console.error('❌ [createForumNotification] Paramètres manquants:', { topicOwnerEmail, topicId, categoryId });
      return { success: false, error: 'Paramètres requis manquants' };
    }

    // S'assurer que l'email est normalisé
    const normalizedEmail = topicOwnerEmail.toLowerCase().trim();
    console.log('📝 [createForumNotification] Création notification:', {
      recipientEmail: normalizedEmail,
      topicId,
      categoryId,
      topicTitle,
      replyAuthorName
    });

    const notificationsRef = collection(db, 'forum_notifications');
    const newNotification = {
      recipientEmail: normalizedEmail,
      topicId: topicId,
      categoryId: categoryId,
      topicTitle: topicTitle,
      replyAuthorName: replyAuthorName,
      replyAuthorEmail: replyAuthorEmail,
      type: 'reply',
      read: false,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(notificationsRef, newNotification);
    console.log('✅ [createForumNotification] Notification créée avec ID:', docRef.id);
    console.log('✅ [createForumNotification] Détails de la notification:', {
      id: docRef.id,
      recipientEmail: normalizedEmail,
      topicId,
      topicTitle
    });
    
    // Vérifier immédiatement que la notification existe
    const verifyRef = doc(db, 'forum_notifications', docRef.id);
    const verifySnap = await getDoc(verifyRef);
    if (verifySnap.exists()) {
      console.log('✅ [createForumNotification] Notification vérifiée dans Firestore:', verifySnap.data());
    } else {
      console.error('❌ [createForumNotification] Notification non trouvée après création!');
    }
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('❌ [createForumNotification] Erreur lors de la création de la notification:', error);
    console.error('❌ [createForumNotification] Détails de l\'erreur:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    return { success: false, error: error.message };
  }
};

// Fonction pour créer une réponse à un topic
export const createForumReply = async (topicId, categoryId, replyData) => {
  // Log IMMÉDIATEMENT pour vérifier que la fonction est appelée
  console.log('🚀🚀🚀 [createForumReply] FONCTION APPELÉE !!!', {
    topicId,
    categoryId,
    replyAuthorEmail: replyData?.authorEmail,
    replyAuthorName: replyData?.authorName,
    hasReplyData: !!replyData,
    replyDataKeys: replyData ? Object.keys(replyData) : []
  });
  
  try {
    if (!categoryId) {
      console.error('❌ [createForumReply] categoryId manquant');
      return { success: false, error: 'categoryId est requis' };
    }
    
    // 1. Créer la réponse
    const repliesRef = collection(db, 'forum', categoryId, 'topics', topicId, 'replies');
    const newReply = {
      content: replyData.content,
      authorEmail: replyData.authorEmail,
      authorName: replyData.authorName,
      createdAt: serverTimestamp()
    };
    
    const replyDocRef = await addDoc(repliesRef, newReply);
    
    // 2. Mettre à jour le topic (incrémenter replyCount et mettre à jour lastActivity)
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);
    
    if (topicSnap.exists()) {
      const currentData = topicSnap.data();
      console.log('📋 [createForumReply] Données du topic:', {
        topicId,
        categoryId,
        authorEmail: currentData.authorEmail,
        title: currentData.title,
        allData: currentData
      });
      
      await updateDoc(topicRef, {
        replyCount: (currentData.replyCount || 0) + 1,
        lastActivity: serverTimestamp()
      });

      // 3. Créer une notification et envoyer un email si l'auteur de la réponse n'est pas le propriétaire du topic
      // Normaliser les emails pour garantir la correspondance
      const topicOwnerEmail = currentData.authorEmail ? String(currentData.authorEmail).toLowerCase().trim() : null;
      const replyAuthorEmail = replyData.authorEmail ? String(replyData.authorEmail).toLowerCase().trim() : null;
      
      console.log('🔔 [createForumReply] Vérification notification:', {
        topicOwnerEmail,
        replyAuthorEmail,
        areDifferent: topicOwnerEmail !== replyAuthorEmail,
        topicTitle: currentData.title,
        hasTopicOwnerEmail: !!topicOwnerEmail,
        hasReplyAuthorEmail: !!replyAuthorEmail,
        topicOwnerEmailType: typeof topicOwnerEmail,
        replyAuthorEmailType: typeof replyAuthorEmail,
        topicOwnerEmailLength: topicOwnerEmail?.length,
        replyAuthorEmailLength: replyAuthorEmail?.length
      });
      
      if (topicOwnerEmail && replyAuthorEmail && topicOwnerEmail !== replyAuthorEmail) {
        console.log('✅ [createForumReply] Condition remplie - Création de la notification pour:', topicOwnerEmail);
        console.log('✅ [createForumReply] Paramètres de la notification:', {
          topicOwnerEmail,
          topicId,
          categoryId,
          topicTitle: currentData.title || 'Sujet sans titre',
          replyAuthorName: replyData.authorName || replyAuthorEmail.split('@')[0],
          replyAuthorEmail
        });
        
        // Créer la notification directement ici pour éviter les problèmes de scope
        try {
          console.log('📞 [createForumReply] Création de la notification directement...');
          
          // Normaliser l'email du propriétaire
          const normalizedRecipientEmail = topicOwnerEmail.toLowerCase().trim();
          
          // Créer la notification dans Firestore
          const notificationsRef = collection(db, 'forum_notifications');
          const newNotification = {
            recipientEmail: normalizedRecipientEmail,
            topicId: topicId,
            categoryId: categoryId,
            topicTitle: currentData.title || 'Sujet sans titre',
            replyAuthorName: replyData.authorName || replyAuthorEmail.split('@')[0],
            replyAuthorEmail: replyAuthorEmail,
            type: 'reply',
            read: false,
            createdAt: serverTimestamp()
          };
          
          console.log('📝 [createForumReply] Notification à créer:', newNotification);
          
          const docRef = await addDoc(notificationsRef, newNotification);
          console.log('✅ [createForumReply] Notification créée avec ID:', docRef.id);
          
          // Vérifier immédiatement que la notification existe
          const verifyRef = doc(db, 'forum_notifications', docRef.id);
          const verifySnap = await getDoc(verifyRef);
          if (verifySnap.exists()) {
            console.log('✅ [createForumReply] Notification vérifiée dans Firestore:', verifySnap.data());
          } else {
            console.error('❌ [createForumReply] Notification non trouvée après création!');
          }
        } catch (notificationError) {
          console.error('❌ [createForumReply] Exception lors de la création de la notification:', notificationError);
          console.error('❌ [createForumReply] Stack:', notificationError.stack);
          console.error('❌ [createForumReply] Détails:', {
            message: notificationError.message,
            code: notificationError.code,
            name: notificationError.name
          });
        }

        // Envoyer un message système au propriétaire du sujet pour l'informer de la nouvelle réponse
        setTimeout(async () => {
          try {
            console.log('📨 [createForumReply] Envoi du message système au propriétaire du sujet...');
            const messageSubject = `Nouvelle réponse sur votre sujet / New reply on your topic: ${currentData.title || 'Sujet sans titre / Untitled topic'}`;
            const messageContent = `Bonjour / Hello,\n\n${replyData.authorName || replyAuthorEmail.split('@')[0]} a répondu à votre sujet "${currentData.title || 'Sujet sans titre'}" dans le forum.\n${replyData.authorName || replyAuthorEmail.split('@')[0]} replied to your topic "${currentData.title || 'Untitled topic'}" in the forum.\n\nVous pouvez consulter la réponse dans le forum.\nYou can view the reply in the forum.\n\nCordialement / Best regards,\nL'équipe ExportService / The ExportService team`;
            
            await sendInternalMessageToUser(
              topicOwnerEmail,
              messageContent,
              messageSubject,
              'Système ExportService'
            );
            console.log('✅ [createForumReply] Message système envoyé avec succès au propriétaire:', topicOwnerEmail);
          } catch (error) {
            console.error('❌ [createForumReply] Erreur lors de l\'envoi du message système (non bloquant):', error);
          }
        }, 200);

        // Envoyer l'email de notification de manière asynchrone (ne bloque pas la création de la réponse)
        // Utiliser setTimeout pour ne pas bloquer le processus principal
        setTimeout(async () => {
          try {
            console.log('📧 [createForumReply] Envoi de l\'email de notification au propriétaire...');
            const emailResult = await sendForumReplyNotificationEmail({
              topicOwnerEmail: topicOwnerEmail,
              topicTitle: currentData.title || 'Sujet sans titre',
              replyAuthorName: replyData.authorName || replyAuthorEmail.split('@')[0],
              replyAuthorEmail: replyAuthorEmail,
              topicId: topicId,
              categoryId: categoryId
            });
            
            if (emailResult.success) {
              console.log('✅ [createForumReply] Email de notification envoyé avec succès au propriétaire:', topicOwnerEmail);
            } else {
              console.warn('⚠️ [createForumReply] Échec de l\'envoi de l\'email (non bloquant):', emailResult.error);
            }
          } catch (error) {
            // Ne pas bloquer la création de la réponse si l'email échoue
            console.error('❌ [createForumReply] Erreur lors de l\'envoi de l\'email de notification (non bloquant):', error);
          }
        }, 100); // Petit délai pour ne pas bloquer la réponse immédiate
      } else {
        console.log('⚠️ [createForumReply] Notification NON créée - Raison:', {
          hasTopicOwnerEmail: !!topicOwnerEmail,
          hasReplyAuthorEmail: !!replyAuthorEmail,
          areSame: topicOwnerEmail === replyAuthorEmail,
          topicOwnerEmail,
          replyAuthorEmail
        });
      }
    } else {
      console.error('❌ [createForumReply] Topic non trouvé ou n\'existe pas');
    }
    
    console.log('✅ [createForumReply] FIN - Réponse créée avec succès');
    return { success: true, id: replyDocRef.id };
  } catch (error) {
    console.error('❌ Erreur lors de la création de la réponse:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour incrémenter le nombre de vues d'un topic
export const incrementTopicViews = async (topicId, categoryId) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }
    
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);
    
    if (topicSnap.exists()) {
      const currentData = topicSnap.data();
      await updateDoc(topicRef, {
        viewCount: (currentData.viewCount || 0) + 1
      });
      return { success: true };
    } else {
      return { success: false, error: 'Topic non trouvé' };
    }
  } catch (error) {
    console.error('❌ Erreur lors de l\'incrémentation des vues:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour la date de dernière ouverture du topic par le propriétaire
export const updateTopicLastViewedByOwner = async (topicId, categoryId, ownerEmail) => {
  try {
    if (!categoryId || !ownerEmail) {
      return { success: false, error: 'categoryId et ownerEmail sont requis' };
    }
    
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);
    
    if (topicSnap.exists()) {
      const currentData = topicSnap.data();
      const normalizedOwnerEmail = ownerEmail.toLowerCase().trim();
      const normalizedAuthorEmail = currentData.authorEmail ? String(currentData.authorEmail).toLowerCase().trim() : null;
      
      // Vérifier que l'utilisateur est bien le propriétaire
      if (normalizedOwnerEmail === normalizedAuthorEmail) {
        console.log('📝 [updateTopicLastViewedByOwner] Mise à jour de lastViewedByOwner pour:', topicId);
        await updateDoc(topicRef, {
          lastViewedByOwner: serverTimestamp()
        });
        console.log('✅ [updateTopicLastViewedByOwner] Date mise à jour avec succès');
        
        // Vérifier que la mise à jour a bien été effectuée
        const verifySnap = await getDoc(topicRef);
        if (verifySnap.exists()) {
          const updatedData = verifySnap.data();
          console.log('✅ [updateTopicLastViewedByOwner] Vérification - lastViewedByOwner:', updatedData.lastViewedByOwner);
        }
        
        return { success: true };
      } else {
        console.log('⚠️ [updateTopicLastViewedByOwner] L\'utilisateur n\'est pas le propriétaire:', {
          ownerEmail: normalizedOwnerEmail,
          authorEmail: normalizedAuthorEmail
        });
        return { success: false, error: 'L\'utilisateur n\'est pas le propriétaire du topic' };
      }
    } else {
      return { success: false, error: 'Topic non trouvé' };
    }
  } catch (error) {
    console.error('❌ Erreur lors de la mise à jour de la dernière ouverture:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour compter les nouvelles réponses depuis la dernière ouverture par le propriétaire
export const getNewRepliesCountSinceLastView = async (topicId, categoryId, ownerEmail) => {
  try {
    if (!categoryId || !ownerEmail) {
      return { success: false, error: 'categoryId et ownerEmail sont requis', count: 0 };
    }
    
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    // Forcer la lecture depuis le serveur pour éviter le cache
    const topicSnap = await getDoc(topicRef);
    
    if (!topicSnap.exists()) {
      return { success: false, error: 'Topic non trouvé', count: 0 };
    }
    
    const topicData = topicSnap.data();
    const normalizedOwnerEmail = ownerEmail.toLowerCase().trim();
    const normalizedAuthorEmail = topicData.authorEmail ? String(topicData.authorEmail).toLowerCase().trim() : null;
    
    // Vérifier que l'utilisateur est bien le propriétaire
    if (normalizedOwnerEmail !== normalizedAuthorEmail) {
      return { success: true, count: 0 }; // Pas le propriétaire, donc pas de nouveaux commentaires
    }
    
    // Récupérer la date de dernière ouverture
    let lastViewedDate = null;
    if (topicData.lastViewedByOwner) {
      if (topicData.lastViewedByOwner.toDate) {
        lastViewedDate = topicData.lastViewedByOwner.toDate();
      } else if (topicData.lastViewedByOwner.seconds) {
        lastViewedDate = new Date(topicData.lastViewedByOwner.seconds * 1000);
      }
    }
    
    console.log('🔍 [getNewRepliesCountSinceLastView] Date de dernière ouverture:', lastViewedDate);
    console.log('🔍 [getNewRepliesCountSinceLastView] topicData.lastViewedByOwner:', topicData.lastViewedByOwner);
    
    // Si pas de date de dernière ouverture, retourner 0 (pas de nouveaux commentaires)
    // car cela signifie que le sujet n'a jamais été ouvert par le propriétaire
    // ou que le champ n'existe pas encore (sujets créés avant l'ajout de cette fonctionnalité)
    if (!lastViewedDate) {
      console.log('⚠️ [getNewRepliesCountSinceLastView] Pas de date de dernière ouverture - retour de 0 (le sujet n\'a pas encore été ouvert)');
      return { success: true, count: 0 };
    }
    
    // Compter les réponses créées après la dernière ouverture
    const repliesRef = collection(db, 'forum', categoryId, 'topics', topicId, 'replies');
    const repliesSnapshot = await getDocs(repliesRef);
    
    let newRepliesCount = 0;
    repliesSnapshot.forEach((replyDoc) => {
      const replyData = replyDoc.data();
      let replyDate = null;
      
      if (replyData.createdAt) {
        if (replyData.createdAt.toDate) {
          replyDate = replyData.createdAt.toDate();
        } else if (replyData.createdAt.seconds) {
          replyDate = new Date(replyData.createdAt.seconds * 1000);
        }
      }
      
      if (replyDate) {
        // Comparer les dates - une réponse est "nouvelle" si elle a été créée APRÈS la dernière ouverture
        // On utilise une marge de 500ms pour éviter les problèmes de timing avec serverTimestamp
        const timeDiff = replyDate.getTime() - lastViewedDate.getTime();
        if (timeDiff > 500) { // Plus de 500ms après la dernière ouverture
          newRepliesCount++;
          console.log('  - Nouvelle réponse trouvée:', {
            replyId: replyDoc.id,
            replyDate: replyDate.toISOString(),
            lastViewed: lastViewedDate.toISOString(),
            diffMs: timeDiff
          });
        } else {
          console.log('  - Réponse non nouvelle (créée avant ou pendant la lecture):', {
            replyId: replyDoc.id,
            replyDate: replyDate.toISOString(),
            lastViewed: lastViewedDate.toISOString(),
            diffMs: timeDiff
          });
        }
      }
    });
    
    console.log('📊 [getNewRepliesCountSinceLastView] Nombre de nouvelles réponses:', newRepliesCount);
    return { success: true, count: newRepliesCount };
  } catch (error) {
    console.error('❌ Erreur lors du comptage des nouvelles réponses:', error);
    return { success: false, error: error.message, count: 0 };
  }
};

// Fonction pour supprimer un topic (admin seulement)
export const deleteForumTopic = async (topicId, categoryId) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }
    
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    await deleteDoc(topicRef);
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la suppression du topic:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour désactiver un sujet approuvé (admin) : met approved à false, le sujet n'est plus visible dans le forum
export const deactivateForumTopic = async (topicId, categoryId) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);
    if (!topicSnap.exists()) {
      return { success: false, error: 'Topic non trouvé' };
    }
    await updateDoc(topicRef, {
      approved: false,
      approvedAt: deleteField(),
      deactivated: true
    });
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la désactivation du topic:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour un topic
export const updateForumTopic = async (topicId, categoryId, topicData) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }

    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    
    // Récupérer les données actuelles du topic pour sauvegarder les anciennes valeurs
    const topicSnap = await getDoc(topicRef);
    if (!topicSnap.exists()) {
      return { success: false, error: 'Topic non trouvé' };
    }
    
    const currentData = topicSnap.data();
    
    // Si approved est explicitement fourni (pour l'approbation admin), l'utiliser
    // Sinon, si skipApproval est true (admin modifie un sujet créé par un admin), ne pas toucher à l'approbation
    // Sinon, c'est une modification utilisateur -> marquer comme non approuvé
    const isAdminApproval = topicData.approved !== undefined;
    const skipApproval = topicData.skipApproval === true && currentData.createdByAdmin === true;
    
    const updateData = {
      title: topicData.title,
      content: topicData.content,
      lastActivity: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Inclure les tags si fournis
    if (topicData.skills !== undefined) {
      updateData.skills = topicData.skills;
    }
    if (topicData.activityDomains !== undefined) {
      updateData.activityDomains = topicData.activityDomains;
    }
    if (topicData.countries !== undefined) {
      updateData.countries = topicData.countries;
    }
    if (topicData.subSectors !== undefined) {
      updateData.subSectors = topicData.subSectors;
    }
    if (topicData.subCountries !== undefined) {
      updateData.subCountries = topicData.subCountries;
    }

    // Gérer l'approbation
    if (isAdminApproval) {
      // C'est une approbation admin
      updateData.approved = topicData.approved;
      if (topicData.approved) {
        updateData.approvedAt = serverTimestamp();
      }
    } else if (skipApproval) {
      // Admin modifie un sujet créé par un admin : on ne touche pas à approved
      // (rien à ajouter)
    } else {
      // C'est une modification utilisateur - marquer comme non approuvé
      updateData.approved = false;
      // Sauvegarder les anciennes valeurs pour permettre la comparaison
      updateData.previousTitle = currentData.title;
      updateData.previousContent = currentData.content;
      updateData.previousSkills = currentData.skills || [];
      updateData.previousActivityDomains = currentData.activityDomains || [];
      updateData.previousCountries = currentData.countries || [];
      updateData.previousSubSectors = currentData.subSectors || [];
      updateData.previousSubCountries = currentData.subCountries || [];
      updateData.modifiedAt = serverTimestamp();
    }

    await updateDoc(topicRef, updateData);
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la mise à jour du topic:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour obtenir tous les sujets en attente d'approbation
export const getPendingForumTopics = async () => {
  try {
    const categoriesResult = await getForumCategories();
    if (!categoriesResult.success) {
      return { success: false, error: 'Impossible de récupérer les catégories', data: [] };
    }
    
    const pendingTopics = [];
    
    // Parcourir toutes les catégories et récupérer les sujets non approuvés
    for (const category of categoriesResult.data) {
      try {
        const topicsRef = collection(db, 'forum', category.id, 'topics');
        // Récupérer tous les sujets et filtrer manuellement (car where avec false peut être problématique)
        const q = query(
          topicsRef,
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Inclure uniquement les sujets non approuvés et non désactivés (les désactivés sont en "Gestion des sujets")
          if (data.approved === false && !data.deactivated) {
            pendingTopics.push({ id: doc.id, categoryId: category.id, ...data });
          }
        });
      } catch (error) {
        // Si erreur d'index, essayer sans orderBy
        if (error.code === 'failed-precondition') {
          try {
            const topicsRef = collection(db, 'forum', category.id, 'topics');
            const querySnapshot = await getDocs(topicsRef);
            
            querySnapshot.forEach((doc) => {
              const data = doc.data();
              if (data.approved === false && !data.deactivated) {
                pendingTopics.push({ id: doc.id, categoryId: category.id, ...data });
              }
            });
          } catch (e) {
            console.error(`Erreur pour la catégorie ${category.id}:`, e);
          }
        } else {
          console.error(`Erreur pour la catégorie ${category.id}:`, error);
        }
      }
    }
    
    // Trier par date de création (plus récents en premier)
    pendingTopics.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || a.createdAt?.seconds || 0;
      const bTime = b.createdAt?.toDate?.() || b.createdAt?.seconds || 0;
      return bTime - aTime;
    });
    
    return { success: true, data: pendingTopics };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des sujets en attente:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Fonction pour approuver un sujet
export const approveForumTopic = async (topicId, categoryId) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }

    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    
    // Récupérer les données actuelles pour vérifier s'il y a des champs previous*
    const topicSnap = await getDoc(topicRef);
    if (!topicSnap.exists()) {
      return { success: false, error: 'Topic non trouvé' };
    }
    
    const currentData = topicSnap.data();
    const updateData: any = {
      approved: true,
      approvedAt: serverTimestamp()
    };
    
    // Supprimer les champs previous* et modifiedAt s'ils existent
    if (currentData.previousTitle !== undefined) {
      updateData.previousTitle = deleteField();
    }
    if (currentData.previousContent !== undefined) {
      updateData.previousContent = deleteField();
    }
    if (currentData.previousSkills !== undefined) {
      updateData.previousSkills = deleteField();
    }
    if (currentData.previousActivityDomains !== undefined) {
      updateData.previousActivityDomains = deleteField();
    }
    if (currentData.previousCountries !== undefined) {
      updateData.previousCountries = deleteField();
    }
    if (currentData.previousSubSectors !== undefined) {
      updateData.previousSubSectors = deleteField();
    }
    if (currentData.previousSubCountries !== undefined) {
      updateData.previousSubCountries = deleteField();
    }
    if (currentData.modifiedAt !== undefined) {
      updateData.modifiedAt = deleteField();
    }
    
    await updateDoc(topicRef, updateData);
    
    // Envoyer un message interne à l'utilisateur pour l'informer que son sujet a été approuvé
    const authorEmail = currentData.authorEmail ? String(currentData.authorEmail).toLowerCase().trim() : null;
    if (authorEmail) {
      setTimeout(async () => {
        try {
          const messageSubject = 'Votre sujet a été approuvé / Your topic has been approved';
          const messageContent = `Bonjour / Hello,\n\nVotre sujet "${currentData.title}" a été approuvé et est maintenant visible dans le forum.\nYour topic "${currentData.title}" has been approved and is now visible in the forum.\n\nVous pouvez le consulter dans la catégorie correspondante.\nYou can view it in the corresponding category.\n\nCordialement / Best regards,\nL'équipe ExportService / The ExportService team`;
          
          await sendInternalMessageToUser(
            authorEmail,
            messageContent,
            messageSubject,
            'Système ExportService'
          );
        } catch (error) {
          console.error('❌ Erreur lors de l\'envoi du message d\'approbation (non bloquant):', error);
        }
      }, 500);
    }
    
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de l\'approbation du sujet:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour rejeter un sujet (le supprimer). rejectionReasons optionnel (liste de motifs pour traçabilité / notification auteur).
export const rejectForumTopic = async (topicId, categoryId, rejectionReasons) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }

    // Supprimer le sujet (rejet = suppression). rejectionReasons peut être utilisé plus tard (ex: notification à l'auteur).
    const result = await deleteForumTopic(topicId, categoryId);
    return result;
  } catch (error) {
    console.error('❌ Erreur lors du rejet du sujet:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour mettre à jour une réponse
export const updateForumReply = async (topicId, categoryId, replyId, replyData) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }

    const replyRef = doc(db, 'forum', categoryId, 'topics', topicId, 'replies', replyId);
    const updateData: any = {
      content: replyData.content,
      updatedAt: serverTimestamp()
    };

    await updateDoc(replyRef, updateData);
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la mise à jour de la réponse:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour supprimer une réponse
export const deleteForumReply = async (topicId, categoryId, replyId) => {
  try {
    if (!categoryId) {
      return { success: false, error: 'categoryId est requis' };
    }
    
    const replyRef = doc(db, 'forum', categoryId, 'topics', topicId, 'replies', replyId);
    await deleteDoc(replyRef);
    
    // Mettre à jour le replyCount du topic
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);
    
    if (topicSnap.exists()) {
      const currentData = topicSnap.data();
      await updateDoc(topicRef, {
        replyCount: Math.max(0, (currentData.replyCount || 1) - 1),
        lastActivity: serverTimestamp()
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors de la suppression de la réponse:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour ajouter/retirer un topic des favoris
export const toggleForumTopicFavorite = async (topicId, categoryId, userEmail) => {
  try {
    if (!categoryId || !userEmail) {
      return { success: false, error: 'categoryId et userEmail sont requis' };
    }

    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);

    if (!topicSnap.exists()) {
      return { success: false, error: 'Topic non trouvé' };
    }

    const currentData = topicSnap.data();
    const favoritedBy = currentData.favoritedBy || [];
    const isFavorited = favoritedBy.includes(userEmail);

    let updatedFavoritedBy;
    if (isFavorited) {
      // Retirer des favoris
      updatedFavoritedBy = favoritedBy.filter(email => email !== userEmail);
    } else {
      // Ajouter aux favoris
      updatedFavoritedBy = [...favoritedBy, userEmail];
    }

    await updateDoc(topicRef, {
      favoritedBy: updatedFavoritedBy,
      favoriteCount: updatedFavoritedBy.length
    });

    return { success: true, isFavorited: !isFavorited, favoriteCount: updatedFavoritedBy.length };
  } catch (error) {
    console.error('❌ Erreur lors de la gestion des favoris:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour vérifier si un topic est dans les favoris d'un utilisateur
export const isTopicFavorited = async (topicId, categoryId, userEmail) => {
  try {
    if (!categoryId || !userEmail) {
      return { success: false, isFavorited: false };
    }

    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);

    if (!topicSnap.exists()) {
      return { success: false, isFavorited: false };
    }

    const favoritedBy = topicSnap.data().favoritedBy || [];
    const isFavorited = favoritedBy.includes(userEmail);

    return { success: true, isFavorited };
  } catch (error) {
    console.error('❌ Erreur lors de la vérification des favoris:', error);
    return { success: false, isFavorited: false };
  }
};

// Cache court pour éviter lectures doublons (Navbar + ForumPage)
function _getCachedForumNotifs(normalizedEmail) {
  if (_forumNotifCache.key === normalizedEmail && Date.now() - _forumNotifCache.ts < FORUM_NOTIF_CACHE_TTL_MS) {
    return _forumNotifCache;
  }
  return null;
}
function _setCachedForumNotifs(normalizedEmail, data, count) {
  _forumNotifCache.key = normalizedEmail;
  _forumNotifCache.data = data;
  _forumNotifCache.count = count;
  _forumNotifCache.ts = Date.now();
}
export function invalidateForumNotifCache() {
  _forumNotifCache.key = null;
  _forumNotifCache.ts = 0;
}

// Fonction pour obtenir les notifications non lues d'un utilisateur (avec cache 30s)
export const getUnreadForumNotifications = async (userEmail) => {
  try {
    if (!userEmail) return { success: false, error: 'Email requis', data: [] };
    const normalizedEmail = userEmail.toLowerCase().trim();
    const cached = _getCachedForumNotifs(normalizedEmail);
    if (cached && cached.data) return { success: true, data: cached.data };

    const notificationsRef = collection(db, 'forum_notifications');
    let q = query(
      notificationsRef,
      where('recipientEmail', '==', normalizedEmail),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );
    let querySnapshot;
    try {
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      if (orderError.code === 'failed-precondition') {
        q = query(notificationsRef, where('recipientEmail', '==', normalizedEmail), where('read', '==', false));
        querySnapshot = await getDocs(q);
      } else throw orderError;
    }
    const notifications = [];
    querySnapshot.forEach((doc) => notifications.push({ id: doc.id, ...doc.data() }));
    if (querySnapshot.size > 0 && notifications[0].createdAt) {
      notifications.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.toDate?.() || b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
    }
    _setCachedForumNotifs(normalizedEmail, notifications, notifications.length);
    return { success: true, data: notifications };
  } catch (error) {
    if (error?.code !== 'permission-denied' && !String(error?.message || '').includes('permission')) {
      console.error('❌ [getUnreadForumNotifications]', error?.message || error);
    }
    return { success: false, error: error.message, data: [] };
  }
};

// Retourne le nombre total de notifications non lues (utilise le cache 30s si disponible)
export const getUnreadForumNotificationCount = async (userEmail) => {
  try {
    if (!userEmail) return { success: true, count: 0 };
    const normalizedEmail = userEmail.toLowerCase().trim();
    const cached = _getCachedForumNotifs(normalizedEmail);
    if (cached != null && cached.count != null) return { success: true, count: cached.count };
    const result = await getUnreadForumNotifications(userEmail);
    if (result.success && result.data) return { success: true, count: result.data.length };
    return { success: true, count: 0 };
  } catch (error) {
    if (error?.code !== 'permission-denied' && !String(error?.message || '').includes('permission')) {
      console.error('❌ [getUnreadForumNotificationCount]', error?.message || error);
    }
    return { success: false, error: error.message, count: 0 };
  }
};

// Fonction pour marquer une notification comme lue
export const markForumNotificationAsRead = async (notificationId) => {
  try {
    invalidateForumNotifCache();
    const notificationRef = doc(db, 'forum_notifications', notificationId);
    await updateDoc(notificationRef, {
      read: true,
      readAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors du marquage de la notification:', error);
    return { success: false, error: error.message };
  }
};

// Fonction pour marquer toutes les notifications comme lues
export const markAllForumNotificationsAsRead = async (userEmail) => {
  try {
    if (!userEmail) {
      return { success: false, error: 'Email requis' };
    }

    // Normaliser l'email (lowercase, trim) pour correspondre à la façon dont les notifications sont créées
    const normalizedEmail = userEmail.toLowerCase().trim();

    const notificationsRef = collection(db, 'forum_notifications');
    const q = query(
      notificationsRef,
      where('recipientEmail', '==', normalizedEmail),
      where('read', '==', false)
    );

    const querySnapshot = await getDocs(q);
    const updatePromises = [];
    querySnapshot.forEach((notificationDoc) => {
      const notificationRef = doc(db, 'forum_notifications', notificationDoc.id);
      updatePromises.push(updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp()
      }));
    });

    await Promise.all(updatePromises);
    return { success: true };
  } catch (error) {
    console.error('❌ Erreur lors du marquage des notifications:', error);
    return { success: false, error: error.message };
  }
};

// Marque toutes les notifications d'un sujet comme lues (1 seule requête getDocs)
export const markTopicNotificationsAsRead = async (topicId, userEmail) => {
  try {
    if (!topicId || !userEmail) return { success: false, error: 'topicId et userEmail sont requis' };
    const normalizedEmail = userEmail.toLowerCase().trim();
    invalidateForumNotifCache();

    const notificationsRef = collection(db, 'forum_notifications');
    const q = query(
      notificationsRef,
      where('recipientEmail', '==', normalizedEmail),
      where('topicId', '==', topicId),
      where('read', '==', false)
    );
    const querySnapshot = await getDocs(q);
    if (querySnapshot.size === 0) return { success: true, count: 0 };

    const updatePromises = [];
    querySnapshot.forEach((notificationDoc) => {
      updatePromises.push(updateDoc(doc(db, 'forum_notifications', notificationDoc.id), {
        read: true,
        readAt: serverTimestamp()
      }));
    });
    await Promise.all(updatePromises);
    return { success: true, count: querySnapshot.size };
  } catch (error) {
    console.error('❌ [markTopicNotificationsAsRead]', error?.message || error);
    return { success: false, error: error.message };
  }
};

// Fonction utilitaire pour créer rétroactivement les notifications pour les réponses existantes
// (Utile pour les réponses créées avant l'implémentation du système de notifications)
export const createNotificationsForExistingReplies = async (topicId, categoryId, topicOwnerEmail) => {
  try {
    if (!topicId || !categoryId || !topicOwnerEmail) {
      return { success: false, error: 'Paramètres requis manquants' };
    }

    // Récupérer le topic
    const topicRef = doc(db, 'forum', categoryId, 'topics', topicId);
    const topicSnap = await getDoc(topicRef);
    
    if (!topicSnap.exists()) {
      return { success: false, error: 'Topic non trouvé' };
    }

    const topicData = topicSnap.data();
    const normalizedOwnerEmail = topicOwnerEmail.toLowerCase().trim();

    // Récupérer toutes les réponses
    const repliesRef = collection(db, 'forum', categoryId, 'topics', topicId, 'replies');
    const repliesSnapshot = await getDocs(repliesRef);

    const notificationsCreated = [];
    const notificationsRef = collection(db, 'forum_notifications');

    // Vérifier quelles notifications existent déjà
    const existingNotificationsQuery = query(
      notificationsRef,
      where('topicId', '==', topicId),
      where('recipientEmail', '==', normalizedOwnerEmail)
    );
    const existingNotificationsSnapshot = await getDocs(existingNotificationsQuery);
    const existingReplyIds = new Set();
    existingNotificationsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.replyId) {
        existingReplyIds.add(data.replyId);
      }
    });

    // Créer les notifications pour les réponses qui n'en ont pas
    for (const replyDoc of repliesSnapshot.docs) {
      const replyData = replyDoc.data();
      const replyAuthorEmail = replyData.authorEmail?.toLowerCase().trim();
      
      // Ne créer une notification que si l'auteur de la réponse n'est pas le propriétaire
      if (replyAuthorEmail && replyAuthorEmail !== normalizedOwnerEmail) {
        // Vérifier si une notification existe déjà pour cette réponse
        if (!existingReplyIds.has(replyDoc.id)) {
          const newNotification = {
            recipientEmail: normalizedOwnerEmail,
            topicId: topicId,
            categoryId: categoryId,
            replyId: replyDoc.id,
            topicTitle: topicData.title || 'Sujet sans titre',
            replyAuthorName: replyData.authorName || replyAuthorEmail.split('@')[0],
            replyAuthorEmail: replyAuthorEmail,
            type: 'reply',
            read: false,
            createdAt: replyData.createdAt || serverTimestamp()
          };

          await addDoc(notificationsRef, newNotification);
          notificationsCreated.push(replyDoc.id);
          console.log('✅ Notification créée pour la réponse:', replyDoc.id);
        }
      }
    }

    return { 
      success: true, 
      notificationsCreated: notificationsCreated.length,
      message: `${notificationsCreated.length} notification(s) créée(s)`
    };
  } catch (error) {
    console.error('❌ Erreur lors de la création rétroactive des notifications:', error);
    return { success: false, error: error.message };
  }
};

// Fonction de test pour vérifier les notifications (à appeler depuis la console)
export const testForumNotifications = async (userEmail) => {
  try {
    const normalizedEmail = userEmail.toLowerCase().trim();
    console.log('🧪 [TEST] Vérification des notifications pour:', normalizedEmail);
    
    // 1. Vérifier toutes les notifications
    const allNotificationsRef = collection(db, 'forum_notifications');
    const allSnapshot = await getDocs(allNotificationsRef);
    console.log('📊 [TEST] Total de notifications dans la base:', allSnapshot.size);
    
    // 2. Vérifier les notifications pour cet utilisateur
    const userNotificationsRef = collection(db, 'forum_notifications');
    const userQ = query(
      userNotificationsRef,
      where('recipientEmail', '==', normalizedEmail)
    );
    const userSnapshot = await getDocs(userQ);
    console.log('📊 [TEST] Notifications pour cet utilisateur:', userSnapshot.size);
    userSnapshot.forEach((doc) => {
      const data = doc.data();
      console.log('  - Notification:', {
        id: doc.id,
        recipientEmail: data.recipientEmail,
        read: data.read,
        topicTitle: data.topicTitle,
        createdAt: data.createdAt
      });
    });
    
    // 3. Vérifier les notifications non lues
    const unreadQ = query(
      userNotificationsRef,
      where('recipientEmail', '==', normalizedEmail),
      where('read', '==', false)
    );
    const unreadSnapshot = await getDocs(unreadQ);
    console.log('📊 [TEST] Notifications non lues:', unreadSnapshot.size);
    
    return {
      total: allSnapshot.size,
      userTotal: userSnapshot.size,
      userUnread: unreadSnapshot.size
    };
  } catch (error) {
    console.error('❌ [TEST] Erreur:', error);
    return { error: error.message };
  }
};

// Exposer la fonction globalement pour les tests (accessible depuis la console)
if (typeof window !== 'undefined') {
  window.testForumNotifications = testForumNotifications;
}

export { db };
